// Core packages
import { formatDate } from "@angular/common";
import {
  AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild
} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import {
  MatDialog, MatOptionSelectionChange, MatPaginator,
  MatSlideToggleChange, MatSort
} from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import {
  AlignmentType, Document, HeadingLevel, HorizontalPositionAlign, HorizontalPositionRelativeFrom, Media, Packer,
  Paragraph,
  TextRun, UnderlineType, VerticalPositionAlign, VerticalPositionRelativeFrom
} from "docx";
import Docxtemplater from 'docxtemplater';
import * as FileSaver from "file-saver";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import moment from "moment";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import PizZip from 'pizzip';
import PizZipUtils from 'pizzip/utils/index.js';
import { combineLatest, fromEvent, merge, Subscription } from "rxjs";
import {
  concatMap, debounceTime, distinctUntilChanged, finalize, startWith, take,
  tap
} from "rxjs/operators";

// Custom packages
import { ViewPlatformNotesDialogComponent } from 'src/app/components/platform-note-card/components/view-platform-notes-dialog/view-platform-notes-dialog.component';
import { PlatformNoteCardComponent } from 'src/app/components/platform-note-card/platform-note-card.component';
import { AccessLogsService } from 'src/app/modules/access-logs/access-logs.service';
import { AuthService } from "src/app/modules/auth/auth.service";
import { BalancesService } from "src/app/modules/balances/balances.service";
import { CommunicationsService } from "src/app/modules/communications/communications.service";
import {
  ADDITIONAL_SERVICE_FLAGS, additionalServiceFee, bindServiceLinks, isAdditionalServiceActive,
  ServiceLinkRefusal, SubscriptionRecord
} from "src/app/modules/companies/additional-services";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { FireSafetyDocsAddDocDialogComponent } from "src/app/modules/companies/components/add-fire-safety-doc-dialog/add-fire-safety-doc-dialog.component";
import { AddPhonebookContactDialogComponent } from "src/app/modules/companies/components/add-phonebook-dialog/add-phonebook-dialog.component";
import { AddSubUserDialogComponent } from "src/app/modules/companies/components/add-sub-user-dialog/add-sub-user-dialog.component";
import { FireSafetyDocsEditDocDialogComponent } from "src/app/modules/companies/components/edit-fire-safety-doc-dialog/edit-fire-safety-doc-dialog.component";
import { EditPhonebookContactDialogComponent } from "src/app/modules/companies/components/edit-phonebook-dialog/edit-phonebook-dialog.component";
import { FireSafetyDocsQrCodeDialogComponent } from "src/app/modules/companies/components/fire-safety-qr-code-dialog/fire-safety-qr-code-dialog.component";
import { GeneratePrivacyDocsDialogComponent } from 'src/app/modules/companies/components/generate-privacy-docs-dialog/generate-privacy-docs-dialog.component';
import { PrintQrcodeDialogComponent } from 'src/app/modules/companies/components/print-qrcode-dialog/print-qrcode-dialog.component';
import { PrivacyStudioDocsAddDocDialogComponent } from 'src/app/modules/companies/components/add-privacy-studio-doc-dialog/add-privacy-studio-doc-dialog.component';
import { PrivacyStudioDocsEditDocDialogComponent } from 'src/app/modules/companies/components/edit-privacy-studio-doc-dialog/edit-privacy-studio-doc-dialog.component';
import { PrivacyDocsLinkDialogComponent } from 'src/app/modules/companies/components/privacy-docs-link-dialog/privacy-docs-link-dialog.component';
import { PrivacyDocsQrCodeDialogComponent } from 'src/app/modules/companies/components/privacy-docs-qr-code-dialog/privacy-docs-qr-code-dialog.component';
import { PrivacyStudioDocsQrCodeDialogComponent } from 'src/app/modules/companies/components/privacy-studio-docs-qr-code-dialog/privacy-studio-docs-qr-code-dialog.component';
import { StudioPrivacyTemplatesDialogComponent } from 'src/app/modules/companies/components/studio-privacy-templates-dialog/studio-privacy-templates-dialog.component';
import { GenericDocsAddDocDialogComponent } from 'src/app/modules/generic-docs/components/add-doc-dialog/add-doc-dialog.component';
import { GenericDocsEditDocDialogComponent } from 'src/app/modules/generic-docs/components/edit-doc-dialog/edit-doc-dialog.component';
import { GenericDocsQrCodeDialogComponent } from 'src/app/modules/generic-docs/components/qr-code-dialog/qr-code-dialog.component';
import { GenericDocsService } from 'src/app/modules/generic-docs/generic-docs.service';
import { PricesService } from "src/app/modules/prices/prices.service";
import { DocumentDownloadDialogComponent } from 'src/app/modules/properties/components/document-download-dialog/document-download-dialog.component';
import { SendEmailDialogComponent } from "src/app/modules/properties/components/send-email-dialog/send-email-dialog.component";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { RolesService } from 'src/app/modules/roles/roles.service';
import { SubscriptionsService } from "src/app/modules/subscriptions/subscriptions.service";
import { SuppliersService } from 'src/app/modules/suppliers/suppliers.service';
import { UsersService } from "src/app/modules/users/users.service";
import { AddCommunicationDialogComponent } from "src/app/shared/components/dialogs/add-communication-dialog/add-communication-dialog.component";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { InfoDialogComponent } from 'src/app/shared/components/dialogs/info-dialog/info-dialog.component';
import { VideoDialogComponent } from 'src/app/shared/components/dialogs/video-dialog/video-dialog.component';
import { ListSetting } from "src/app/shared/interfaces/list-setting.interface";
import { ICompany } from "src/app/shared/models/company/company.interface";
import { Company } from "src/app/shared/models/company/company.model";
import { IPrice } from "src/app/shared/models/price/price.interface";
import { PropertyInterface } from "src/app/shared/models/property/property.interface";
import { IArchediSubscription } from "src/app/shared/models/subscription/subscription.interface";
import { IUser } from "src/app/shared/models/user/user.interface";
import { User } from "src/app/shared/models/user/user.model";
import { DateTimeFormatPipe } from "src/app/shared/pipes/date-format.pipe";
import { ConfigService } from "src/app/shared/services/config.service";
import { FireSafetyDocsService } from 'src/app/shared/services/fire-safety-docs.service';
import { HelperService } from "src/app/shared/services/helper.service";
import { ListsService } from "src/app/shared/services/lists.service";
import {
  DEFAULT_PRIVACY_DOC_EXTENSION,
  PRIVACY_DOC_MIME_EXTENSIONS,
  PrivacyDocsService,
  PROPERTY_PRIVACY_TEMPLATE,
  SUPPLIER_PRIVACY_TEMPLATE,
} from 'src/app/shared/services/privacy-docs.service';
import { PrivacyStudioDocsService } from 'src/app/shared/services/privacy-studio-docs.service';
import { InformativeNoteService } from 'src/app/modules/companies/informative-note.service';
import { ThemeService } from "src/app/shared/services/theme.service";
import { VideoViewService } from "src/app/shared/services/video-view.service";
import { AccessLogsDataSource } from 'src/app/shared/utils/data-sources/access-logs-data-source';
import { BalancesDataSource } from "src/app/shared/utils/data-sources/balances-data-source";
import { FireSafetyDocsDataSource } from 'src/app/shared/utils/data-sources/fire-safety-docs-source';
import { GenericDocsDataSource } from 'src/app/shared/utils/data-sources/generic-docs-source';
import { PhonebookDataSource } from "src/app/shared/utils/data-sources/phonebook-data-source";
import { PrivacyDocsDataSource } from 'src/app/shared/utils/data-sources/privacy-docs-source';
import { PrivacyStudioDocsDataSource } from 'src/app/shared/utils/data-sources/privacy-studio-docs-source';
import { SubPropertiesDataSource } from "src/app/shared/utils/data-sources/sub-properties-data-source";
import { SubUsersDataSource } from "src/app/shared/utils/data-sources/sub-users-data-source";
import { SuppliersInvitationDataSource } from "src/app/shared/utils/data-sources/suppliers-invitation-data-source";
import { SuppliersNewDataSource } from 'src/app/shared/utils/data-sources/suppliers-new-data-source';
import { VideoViewDataSource } from "src/app/shared/utils/data-sources/video-view-data-source";

@Component({
  selector: "app-company-detail",
  templateUrl: "./company-detail.page.html",
  styleUrls: ["./company-detail.page.scss"],
})
export class CompanyDetailComponent
  implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  @ViewChild('historicalNotes', { static: false }) historicalNotes: PlatformNoteCardComponent;
  historicalReload = false;

  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserMaster = false;
  loggedUserAdminOrOperator = false;
  currentRole: any;
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  company: Company = new Company();
  companyData: ICompany;
  availableSubUsers = [];
  detailCardIsLoading = true;
  deleteCardIsLoading = true;
  subUsersCardIsLoading = true;
  subPropertiesCardIsLoading = true;
  suppliersCardIsLoading = true;
  phonebookCardIsLoading = true;

  companyId = this.activatedRoute.snapshot.params.companyId;

  // BEGIN - Add company form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  formSC: FormGroup = new FormGroup({});
  model: any = {};
  modelSC: any = {};
  modelSCInit: any = {};
  anagraphicFields: FormlyFieldConfig[] = [];
  contractFields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  priceFields: any = {};
  // END - Add company form

  // BEGIN - Delete company
  deleteBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.deactivate"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "warn",
    spinnerColor: "warn",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-times",
      inline: true,
    },
  };
  reactivateBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.activate"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-check",
      inline: true,
    },
  };
  // END - Delete company

  // BEGIN - SubUsers table properties
  subUsersDisplayedColumns: string[] = [
    "index", 
    "role",
    "firstName",
    "lastName",
    "email",
    "createdAt",
    "actions",
  ];
  subUsersDataSource: SubUsersDataSource;
  @ViewChild("subUsersSort", { static: false }) subUsersSort: MatSort;
  @ViewChild("subUsersPaginator", { static: false })
  subUsersPaginator: MatPaginator;
  // END - SubUsers table properties

  // BEGIN - SubProperties table properties
  subPropertiesDisplayedColumns: string[] = [
    "index",
    "hasNotes",
    "name",
    "masterCode",
    "address",
    "addressCity",
    "admin",
    "status",
    "subscriptionEndDate",
    "totalDocsInserted",
    "structureOpen",
    // "actions",
  ];
  subPropertiesDataSource: SubPropertiesDataSource;
  @ViewChild("subPropertiesSort", { static: false }) subPropertiesSort: MatSort;
  @ViewChild("subPropertiesPaginator", { static: false })
  subPropertiesPaginator: MatPaginator;
  @ViewChild("subPropertiesInput", { static: false })
  subPropertiesInput: ElementRef;
  adminFilter: FormControl = new FormControl("");
  ownerFilter: FormControl = new FormControl("");
  typeFilter: FormControl = new FormControl("");
  nameFilter: FormControl = new FormControl("");
  masterCodeFilter: FormControl = new FormControl("");
  addressFilter: FormControl = new FormControl("");
  addressCityFilter: FormControl = new FormControl("");
  subscriptionEndDateFilter: FormControl = new FormControl("");
  totalDocsInsertedFilter: FormControl = new FormControl("");
  propertiesStatusFilter: FormControl = new FormControl("");
  adminIsLoading = false;
  ownerIsLoading = false;
  availableAdmins: User[] = [];
  availableOwners: User[] = [];
  availableTypes = [];
  // END - SubProperties table properties

  
  selectedSuppliers: any[] = [];
  // BEGIN - Table suppliers
  suppliersDisplayedColumns: string[] = ['index', 'checkbox', 'companyName', 'status', 'employees', 'startCompanyDate', 'registration', 'durc', 'creditLicence', 'credits', 'qualifications', 'register', 'invitationDate', 'actions'];  // 'email', 'pec', 
  suppliersDataSource: SuppliersNewDataSource;
  @ViewChild('suppliersSort', { static: false }) suppliersSort: MatSort;
  @ViewChild('suppliersPaginator', { static: false }) suppliersPaginator: MatPaginator;
  companyNameFilter: FormControl = new FormControl('');
  qualificationsFilter: FormControl = new FormControl('');
  startCompanyDateFilter: FormControl = new FormControl('');
  registrationFilter: FormControl = new FormControl('');
  durcFilter: FormControl = new FormControl('');
  creditLicenceFilter: FormControl = new FormControl('');
  creditsFilter: FormControl = new FormControl('');
  statusSuppliersFilter: FormControl = new FormControl('');
  employeesFilter: FormControl = new FormControl('');
  registerFilter: FormControl = new FormControl('');
  invitationDateFilter: FormControl = new FormControl('');
  // END - Table suppliers
  
  statusOptions = [
    {value: 'notDefined', label: this.translateService.instant('suppliers.status.notDefined')},
    {value: 'active', label: this.translateService.instant('suppliers.status.active')},
    {value: 'registered', label: this.translateService.instant('suppliers.status.registered')},
    {value: 'inactive', label: this.translateService.instant('suppliers.status.inactive')},
    {value: 'suspended', label: this.translateService.instant('suppliers.status.suspended')},
    {value: 'inRegistration', label: this.translateService.instant('suppliers.status.inRagistration')},
    {value: 'ceased', label: this.translateService.instant('suppliers.status.ceased')},
    {value: 'notRegistered', label: this.translateService.instant('suppliers.status.notRegistered')},
  ];

  // BEGIN - phonebook table properties
  phonebookDisplayedColumns: string[] = [
    "index", 
    "firstName",
    "lastName",
    "email",
    "createdAt",
    "actions",
  ];
  phonebookDataSource: PhonebookDataSource;
  @ViewChild("phonebookSort", { static: false }) phonebookSort: MatSort;
  @ViewChild("phonebookPaginator", { static: false })
  phonebookPaginator: MatPaginator;
  // END - phonebook table properties

  // START - Access List Table properties
  displayedColumnsAccessList: string[] = ['user', 'email', 'company', 'type', 'role', 'createdAt'];
  accessListDataSource: AccessLogsDataSource;
  @ViewChild('accessListSort', { static: true }) accessListSort: MatSort;
  @ViewChild("accessListPaginator", { static: true }) accessListPaginator: MatPaginator;
  // @ViewChild('input', { static: true }) input: ElementRef;
  userFilter: FormControl = new FormControl('');
  typeAccessFilter: FormControl = new FormControl('');
  emailFilter: FormControl = new FormControl('');
  companyFilter: FormControl = new FormControl('');
  ipAddressFilter: FormControl = new FormControl('');
  roleFilter: FormControl = new FormControl('');
  createdAtAccessFilter: FormControl = new FormControl('');
  accessListCardIsLoading = true;
  filterRoleTypes: any[] = [];
  // END - Access List Table properties

  // BEGIN - balances table properties
  balancesDisplayedColumns: string[] = [
    "index", 
    "property",
    "number",
    "status",
    "total",
    "discount",
    "createdAt",
    "actions",
  ];
  balancesDataSource: BalancesDataSource;
  @ViewChild("balancesSort", { static: false }) balancesSort: MatSort;
  @ViewChild("balancesPaginator", { static: false })
  balancesPaginator: MatPaginator;
  @ViewChild("balancesInput", { static: false }) balancesInput: ElementRef;
  numberFilter: FormControl = new FormControl("");
  propertyFilter: FormControl = new FormControl("");
  statusFilter: FormControl = new FormControl("");
  totalFilter: FormControl = new FormControl("");
  discountFilter: FormControl = new FormControl("");
  createdAtFilter: FormControl = new FormControl("");
  balancesCardIsLoading = true;
  // END - balances table properties

  dateTimeFormat: DateTimeFormatPipe;
  prices: IPrice[] = [];
  availableSubscriptions: any[];
  availableSubscriptionsKeys: any[];

  defaultStartDate: string;
  defaultEndDate: string;

  isInit = true;

  enableEdit = false;

  propertyIsLoading = true;
  availableProperties: PropertyInterface[] = [];

  archediSubscriptions: IArchediSubscription[] = [];

  previousSubscription: any = {};

  // List of codes
  firstScanCodes = this.configService.availableScanCodes;
  firstFixedPackCodes = this.configService.availableFixedPackageCodes;
  scanInsNextCodesForfait = this.configService.availableInsCodesNextForfait;
  scanNextCodesForfait = this.configService.availableScanCodesNextForfait;

  // disclaimer
  disclaimer = false;
  disclaimerAt = '';
  disclaimerUser = '';

  
  // BEGIN - Table safetyVideoSuppliers
  safetyVideoSuppliersDisplayedColumns: string[] = [
    "index", 
    "firstName",
    "lastName",
    "companyName",
    "createdAt",
  ];
  safetyVideoSuppliersDataSource: VideoViewDataSource;
  @ViewChild("safetyVideoSuppliersSort", { static: false }) safetyVideoSuppliersSort: MatSort;
  @ViewChild("safetyVideoSuppliersPaginator", { static: false }) safetyVideoSuppliersPaginator: MatPaginator;
  @ViewChild("safetyVideoSuppliersInput", { static: false }) safetyVideoSuppliersInput: ElementRef;
  safetyVideoSuppliersFirstNameFilter: FormControl = new FormControl("");
  safetyVideoSuppliersLastNameFilter: FormControl = new FormControl("");
  safetyVideoSuppliersCompanyNameFilter: FormControl = new FormControl("");
  safetyVideoSuppliersCreatedAtFilter: FormControl = new FormControl("");
  // END - Table safetyVideoSuppliers
  safetyVideoSuppliersQrcodeData: string;
  safetyVideoSuppliersCardIsLoading = true;

  // BEGIN - Table safetyVideoResidents
  safetyVideoResidentsDisplayedColumns: string[] = [
    "index", 
    "firstName",
    "lastName",
    "companyName",
    "createdAt",
  ];
  safetyVideoResidentsDataSource: VideoViewDataSource;
  @ViewChild("safetyVideoResidentsSort", { static: false }) safetyVideoResidentsSort: MatSort;
  @ViewChild("safetyVideoResidentsPaginator", { static: false }) safetyVideoResidentsPaginator: MatPaginator;
  @ViewChild("safetyVideoResidentsInput", { static: false }) safetyVideoResidentsInput: ElementRef;
  safetyVideoResidentsFirstNameFilter: FormControl = new FormControl("");
  safetyVideoResidentsLastNameFilter: FormControl = new FormControl("");
  safetyVideoResidentsCompanyNameFilter: FormControl = new FormControl("");
  safetyVideoResidentsCreatedAtFilter: FormControl = new FormControl("");
  // END - Table safetyVideoResidents
  safetyVideoResidentsQrcodeData: string;
  safetyVideoResidentsCardIsLoading = true;

  // BEGIN - Table generic docs
  genericDocsDisplayedColumns: string[] = ['index', 'checkbox', 'name', 'description', 'createdAt', 'actions'];
  genericDocsDataSource: GenericDocsDataSource;
  @ViewChild('genericDocsSort', { static: false }) genericDocsSort: MatSort;
  @ViewChild('genericDocsPaginator', { static: false }) genericDocsPaginator: MatPaginator;
  // @ViewChild('genericDocsInput', { static: false }) genericDocsInput: ElementRef;
  genericDocsNameFilter: FormControl = new FormControl('');
  genericDocsDescriptionFilter: FormControl = new FormControl('');
  genericDocsCreatedAtFilter: FormControl = new FormControl('');
  // END - Table generic docs
  genericDocsCardIsLoading = true;
  selectedGenericDocs: any[] = [];

  // BEGIN - Table fire safety docs - default
  fireSafetyDocsDefaultDataSource = [
    {
      _id: "68e62051087b3792d6b89aaa",
      name: this.translateService.instant("companies.fields.fireSafetyDefaultDocs.1.name"),
      description: this.translateService.instant("companies.fields.fireSafetyDefaultDocs.1.description"),
      createdAt: new Date(this.translateService.instant("companies.fields.fireSafetyDefaultDocs.1.createdAt")),
      files: [
        {
          _id: "6634d6c7c3c77915076f5c8c",
          originalName: "GSA-SAG-LP-ALL.pdf",
          filename: "GSA-SAG-LP-ALL.pdf",
          path: "uploads/firesafetydocs/GSA-SAG-LP-ALL.pdf",
          mimetype: "application/pdf",
          size: 2056192,
          createdBy: "5ef210e3e5da4742563f4c75",
          createdAt: "2024-05-17T12:21:25.740+00:00",
          updatedAt: "2024-05-17T12:21:25.740+00:00"
        }
      ]
    }
  ];
  fireSafetyDocsefaultDisplayedColumns: string[] = ['index', 'name', 'description', 'createdAt', 'actions'];
  // END - Table fire safety docs - default

  // BEGIN - Table fire safety docs
  fireSafetyDocsDisplayedColumns: string[] = ['index', 'checkbox', 'name', 'description', 'createdAt', 'actions'];
  fireSafetyDocsDataSource: FireSafetyDocsDataSource;
  @ViewChild('fireSafetyDocsSort', { static: false }) fireSafetyDocsSort: MatSort;
  @ViewChild('fireSafetyDocsPaginator', { static: false }) fireSafetyDocsPaginator: MatPaginator;
  // @ViewChild('fireSafetyDocsInput', { static: false }) fireSafetyDocsInput: ElementRef;
  fireSafetyDocsNameFilter: FormControl = new FormControl('');
  fireSafetyDocsDescriptionFilter: FormControl = new FormControl('');
  fireSafetyDocsCreatedAtFilter: FormControl = new FormControl('');
  // END - Table fire safety docs

  // BEGIN - Table invitation
  invitationDisplayedColumns: string[] = [
    "index",
    "companyName",
    "vatNumber",
    "email",
    "pec",
    "register"
  ];
  invitationDataSource: SuppliersInvitationDataSource;
  @ViewChild('invitationSort', { static: false }) invitationSort: MatSort;
  @ViewChild('invitationPaginator', { static: false }) invitationPaginator: MatPaginator;

  invitationSupplierFilter: FormControl = new FormControl("");
  invitationVatNumberFilter: FormControl = new FormControl("");
  invitationEmailFilter: FormControl = new FormControl("");
  invitationPecFilter: FormControl = new FormControl("");
  invitationRegisterFilter: FormControl = new FormControl("");

  invitationCardIsLoading = true;
  // END - Table invitation

  fireSafetyDocsCardIsLoading = true;
  selectedFireSafetyDocs: any[] = [];

  // BEGIN - Table privacy docs
  privacyDocsDisplayedColumns: string[] = ['index', 'type', 'description', 'createdAt', 'actions'];
  privacyDocsDataSource: PrivacyDocsDataSource;
  @ViewChild('privacyDocsSort', { static: false }) privacyDocsSort: MatSort;
  @ViewChild('privacyDocsPaginator', { static: false }) privacyDocsPaginator: MatPaginator;
  // END - Table privacy docs

  // BEGIN - Table "Nota informativa" docs (condo-owner privacy notice only)
  informativeNoteDocsDataSource: PrivacyDocsDataSource;
  @ViewChild('informativeNoteDocsSort', { static: false }) informativeNoteDocsSort: MatSort;
  @ViewChild('informativeNoteDocsPaginator', { static: false }) informativeNoteDocsPaginator: MatPaginator;
  // END - Table "Nota informativa" docs
  privacyDocsCardIsLoading = true;
  privacyDocsSendPecDisabled = false;

  // BEGIN - Table privacy studio docs
  privacyStudioDocsDisplayedColumns: string[] = ['index', 'checkbox', 'type', 'description', 'createdAt', 'actions'];
  privacyStudioDocsDataSource: PrivacyStudioDocsDataSource;
  @ViewChild('privacyStudioDocsSort', { static: false }) privacyStudioDocsSort: MatSort;
  @ViewChild('privacyStudioDocsPaginator', { static: false }) privacyStudioDocsPaginator: MatPaginator;
  privacyStudioDocsTypeFilter: FormControl = new FormControl('');
  privacyStudioDocsDescriptionFilter: FormControl = new FormControl('');
  privacyStudioDocsCreatedAtFilter: FormControl = new FormControl('');
  selectedPrivacyStudioDocs: any[] = [];
  // END - Table privacy studio docs

  nfcAccess = true;
  suppliers = true;

  disableSendEmail = false;
  showMasterCode = false;

  hiddenCardSuppliers = false;
  hiddenCardInvitation = false;
  hiddenCardPrivacy = true;
  privacySagActive = false;
  privacyActive = false;
  // "Nota informativa": condo-owner privacy access key without Privacy SAG
  informativeNoteActive = false;
  // Saved state of each service, shown as a badge to Admin / Operator (@see setActiveServices)
  activeServices: {
    suppliers: boolean;
    safetyVideoSuppliers: boolean;
    safetyVideoResidents: boolean;
    fireSafety: boolean;
    informativeNote: boolean;
    privacy: boolean;
  } = {
    suppliers: false,
    safetyVideoSuppliers: false,
    safetyVideoResidents: false,
    fireSafety: false,
    informativeNote: false,
    privacy: false,
  };
  informativeNoteDocIsLoading = false;

  /** Only "Nota informativa" (no Privacy / Privacy SAG): supplier PEC sending is not part of it */

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private companiesService: CompaniesService,
    private router: Router,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    public dialog: MatDialog,
    public newMessageDialog: MatDialog,
    private cookieService: CookieService,
    private pricesService: PricesService,
    private subscriptionsService: SubscriptionsService,
    private balancesService: BalancesService,
    private listsService: ListsService,
    private propertiesService: PropertiesService,
    private accessLogsService: AccessLogsService,
    private rolesService: RolesService,
    private usersService: UsersService,
    private videoViewService: VideoViewService,
    private genericDocsService: GenericDocsService,
    private fireSafetyDocsService: FireSafetyDocsService,
    private privacyDocsService: PrivacyDocsService,
    private privacyStudioDocsService: PrivacyStudioDocsService,
    private communicationsService: CommunicationsService,
    private suppliersService: SuppliersService,
    private informativeNoteService: InformativeNoteService,
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("companies.detail.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => {
        this.loggedUser = res;
        this.currentRole = this.loggedUser.userRoles.find((obj: any) => {
          return (
            obj._id.toString() ===
            this.authService.loggedUserRequestedRole$.getValue().toString()
          );
        });
        
        if (
          this.currentRole.role.type === "master" ||
          this.currentRole.role.type === "collaborator"
        ) {
          this.loggedUserMaster = true;
        }

        if (
          this.currentRole.role.type === "admin" ||
          this.currentRole.role.type === "operator"
        ) {
          this.loggedUserAdminOrOperator = true;
        }
      })
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Init dataSources
    this.subUsersDataSource = new SubUsersDataSource(
      this.companiesService,
      this.helperService
    );
    this.subPropertiesDataSource = new SubPropertiesDataSource(
      this.companiesService,
      this.helperService
    );
    this.phonebookDataSource = new PhonebookDataSource(
      this.companiesService,
      this.helperService
    );
    this.balancesDataSource = new BalancesDataSource(
      this.balancesService,
      this.helperService
    );
    this.accessListDataSource = new AccessLogsDataSource(this.accessLogsService, this.helperService);
    this.filterRoleTypes = this.rolesService.roleTypes;
    this.roleFilter.reset();

    // Se default start and end date
    this.defaultStartDate = moment().toISOString();
    this.defaultEndDate = moment().add(1, "years").subtract(1, 'days').toISOString();

    this.getPrices();

    this.safetyVideoSuppliersDataSource = new VideoViewDataSource(
      this.videoViewService,
      this.helperService
    );

    this.safetyVideoResidentsDataSource = new VideoViewDataSource(
      this.videoViewService,
      this.helperService
    );

    this.genericDocsDataSource = new GenericDocsDataSource(
      this.genericDocsService, 
      this.helperService
    );

    this.fireSafetyDocsDataSource = new FireSafetyDocsDataSource(
      this.fireSafetyDocsService,
      this.helperService
    );

    this.privacyDocsDataSource = new PrivacyDocsDataSource(
      this.privacyDocsService,
      this.helperService
    );

    this.informativeNoteDocsDataSource = new PrivacyDocsDataSource(
      this.privacyDocsService,
      this.helperService
    );

    this.privacyStudioDocsDataSource = new PrivacyStudioDocsDataSource(
      this.privacyStudioDocsService,
      this.helperService
    );

    this.suppliersDataSource = new SuppliersNewDataSource(this.suppliersService, this.helperService, false, this.translateService);

    this.invitationDataSource = new SuppliersInvitationDataSource(
      this.suppliersService,
      this.helperService
    );
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Get requested company data
    this.subscriptions.push(
      this.companiesService.get(this.companyId).subscribe(
        (res) => {
          if (res.status) {
            // Set company data
            this.company.setData(res.data);
            this.companyData = res.data;
            // The "Nota informativa" puts the condo-owner access key in the Privacy section
            this.hiddenCardPrivacy = !(
              res.data.subscription &&
              (res.data.subscription.privacy || res.data.subscription.privacySag || res.data.subscription.informativeNote)
            );
            this.privacySagActive = !!(
              res.data.subscription && res.data.subscription.privacySag
            );
            this.privacyActive = !!(
              res.data.subscription && res.data.subscription.privacy
            );
            this.informativeNoteActive = !!(
              res.data.subscription && res.data.subscription.informativeNote
            );
            this.setActiveServices(res.data.subscription);
            for (const prop in res.data.subscription) {
              this.previousSubscription[prop] =
                res.data.subscription[prop];
              if (prop === 'nfcAccess') {
                this.nfcAccess = res.data.subscription[prop];
                if (!this.nfcAccess) {
                  let index = this.suppliersDisplayedColumns.findIndex(
                    (el: string) => el === 'checkbox'
                  );
                  this.suppliersDisplayedColumns.splice(index, 1);
                  index = this.suppliersDisplayedColumns.findIndex(
                    (el: string) => el === 'register'
                  );
                  this.suppliersDisplayedColumns.splice(index, 1);
                  index = this.suppliersDisplayedColumns.findIndex(
                    (el: string) => el === 'invitationDate'
                  );
                  this.suppliersDisplayedColumns.splice(index, 1);
                }
              }
              if (prop === 'suppliers') {
                this.suppliers = res.data.subscription[prop];
              }
            }
            if (this.suppliers) {
              this.hiddenCardInvitation = true;
              this.hiddenCardSuppliers = false;
            } else if (this.nfcAccess) {
              this.hiddenCardInvitation = false;
              this.hiddenCardSuppliers = true;
            }
            if (res.data.disableSendEmailNfcAccess) {
              this.disableSendEmail = res.data.disableSendEmailNfcAccess;
            }
            if (res.data.showMasterCode) {
              this.showMasterCode = res.data.showMasterCode;
            }
            if (this.loggedUserMaster && !this.showMasterCode) {
              const masterCodeIndex = this.subPropertiesDisplayedColumns.findIndex(
                (el: string) => el === 'masterCode'
              );
              if (masterCodeIndex > -1) {
                this.subPropertiesDisplayedColumns.splice(masterCodeIndex, 1);
              }
            }

            // Disclaimer
            if (
              res.data.disclaimer &&
              res.data.disclaimer.check1 &&
              res.data.disclaimer.check2 &&
              res.data.disclaimer.check3 &&
              res.data.disclaimer.check4
            ) {
              this.disclaimer = true;
              this.disclaimerAt = res.data.disclaimer.dateCheck1;
              this.subscriptions.push(
                this.usersService.get(res.data.disclaimer.check1UpdatedBy).subscribe(
                  (res) => {
                    if (res.status) {
                      this.disclaimerUser = `${res.data.firstName} ${res.data.lastName}`;
                    }
                  }
                )
              );
            }

            this.removeIdsFromPrices(this.company.get("subscription.prices"));

            // Save subscription key value for future uses
            // this.previousKey = res.data.subscription && res.data.subscription.key ? res.data.subscription.key : undefined;

            // Stop loading in delete form
            this.deleteCardIsLoading = false;

            // Init edit form
            setTimeout(() => {
              this.initForm();
            }, 500);

            // Make detail form card visible by disabling his card-spinner
            this.detailCardIsLoading = false;

            // If there are no subUsers, let's create an empty array
            if (!this.company.get("subUsers")) {
              this.company.set("subUsers", []);
            }
            // Init subUsers
            this.initSubUsersDataTable();
            // Stop loading subUsers card
            this.subUsersCardIsLoading = false;

            // If there are no subProperties, let's create an empty array
            if (!this.company.get("subProperties")) {
              this.company.set("subProperties", []);
            }
            // Init subProperties
            this.initSubPropertiesDataTable();
            // Stop loading subProperties card
            this.subPropertiesCardIsLoading = false;

            // Init suppliers
            this.initSuppliersDataTable();
            this.suppliersCardIsLoading = false;

            // Init phonebook
            this.initPhonebookDataTable();
            // Stop loading phonebook card
            this.phonebookCardIsLoading = false;

            // Init AccessList
            this.initAccessListDataTable();
            // Stop loading phonebook card
            this.accessListCardIsLoading = false;

            // Subscribe to listsService and update this list settings
            if (this.loggedUserPermissions.includes("canReadBalancesList")) {
              this.subscribeToListsSettings("balances");
              this.initBalancesDataTable();
            }
            this.balancesCardIsLoading = false;

            this.getAvailableProperties();

            this.initSafetyVideoSuppliersDataTable();
            this.safetyVideoSuppliersCardIsLoading = false;

            let section = "safetyVideoSuppliers";
            let host = this.configService.frontEndHost;
            this.safetyVideoSuppliersQrcodeData = `${host}/qrcode/${this.companyId}/${section}`;

            this.initSafetyVideoResidentsDataTable();
            this.safetyVideoResidentsCardIsLoading = false;

            section = "safetyVideoResidents";
            host = this.configService.frontEndHost;
            this.safetyVideoResidentsQrcodeData = `${host}/qrcode/${this.companyId}/${section}`;

            this.initGenericDocsDataTable();
            this.genericDocsCardIsLoading = false;

            this.initFireSafetyDocsDataTable();
            this.fireSafetyDocsCardIsLoading = false;

            this.initPrivacyDocsDataTable();
            this.initInformativeNoteDocsDataTable();
            this.privacyDocsCardIsLoading = false;

            this.initPrivacyStudioDocsDataTable();

            // Init invitationsuppliers
            this.initInvitationDataTable();
            this.invitationCardIsLoading = false;

            this.isInit = false;
            return;
          }

          // Something bad happened
          // redirect user to home
          this.router.navigate([this.lang, "app", "companies", "list"]);
        },
        (err) => {
          // if we came here users with given userId do not exist
          this.router.navigate([this.lang, "errors", "404"]);
        }
      )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());

    // Push current list settings to the listsService
    if (this.loggedUserPermissions.includes("canReadBalancesList")) {
      this.listsService.setListSetting(
        "balances",
        this.balancesPaginator.pageIndex.valueOf(),
        this.balancesPaginator.pageSize.valueOf(),
        {
          general: this.balancesInput.nativeElement.value,
          number: this.numberFilter.value,
          property: this.propertyFilter.value,
          status: this.statusFilter.value,
          total: this.totalFilter.value,
          discount: this.discountFilter.value,
          createdAt: this.createdAtFilter.value,
        }
      );
    }

    // Push current list settings to the listsService
    this.listsService.setListSetting(
      "properties",
      this.subPropertiesPaginator.pageIndex.valueOf(),
      this.subPropertiesPaginator.pageSize.valueOf(),
      {
        general: this.subPropertiesInput.nativeElement.value,
        admin: this.adminFilter.value,
        owner: this.ownerFilter.value,
        type: this.typeFilter.value,
        name: this.nameFilter.value,
        masterCode: this.masterCodeFilter.value,
        address: this.addressFilter.value,
        addressCity: this.addressCityFilter.value,
        subscriptionEndDate: this.subscriptionEndDateFilter.value
          ? moment(this.subscriptionEndDateFilter.value).toISOString()
          : null,
        totalDocsInserted: this.totalDocsInsertedFilter.value,
        status: this.propertiesStatusFilter.value,
      }
    );

    // Push current list settings to the listsService
    this.listsService.setListSetting(
      'accessLogs',
      this.accessListPaginator.pageIndex.valueOf(),
      this.accessListPaginator.pageSize.valueOf(),
      {
        general: '', // this.input.nativeElement.value,
        user: this.userFilter.value,
        type: this.typeAccessFilter.value,
        email: this.emailFilter.value,
        company: this.companyFilter.value,
        ipAddress: this.ipAddressFilter.value,
        role: this.roleFilter.value,
        createdAt: this.createdAtAccessFilter.value,
      }
    );

    if (this.safetyVideoSuppliersPaginator) {
      // Push current list settings to the listsService
      this.listsService.setListSetting(
        "safetyVideoSuppliers",
        this.safetyVideoSuppliersPaginator.pageIndex.valueOf(),
        this.safetyVideoSuppliersPaginator.pageSize.valueOf(),
        {
          firstNameFilter: this.safetyVideoSuppliersFirstNameFilter.value,
          lastNameFilter: this.safetyVideoSuppliersLastNameFilter.value,
          companyNameFilter: this.safetyVideoSuppliersCompanyNameFilter.value,
          createdAtFilter: this.safetyVideoSuppliersCreatedAtFilter.value,
        }
      );
    }

    if (this.safetyVideoResidentsPaginator) {
      // Push current list settings to the listsService
      this.listsService.setListSetting(
        "safetyVideoResidents",
        this.safetyVideoResidentsPaginator.pageIndex.valueOf(),
        this.safetyVideoResidentsPaginator.pageSize.valueOf(),
        {
          firstNameFilter: this.safetyVideoResidentsFirstNameFilter.value,
          lastNameFilter: this.safetyVideoResidentsLastNameFilter.value,
          companyNameFilter: this.safetyVideoResidentsCompanyNameFilter.value,
          createdAtFilter: this.safetyVideoResidentsCreatedAtFilter.value,
        }
      );
    }

    if (this.genericDocsPaginator) {
      // Push current list settings to the listsService
      this.listsService.setListSetting(
        'genericDocs',
        this.genericDocsPaginator.pageIndex.valueOf(),
        this.genericDocsPaginator.pageSize.valueOf(),
        {
          name: this.genericDocsNameFilter.value,
          description: this.genericDocsDescriptionFilter.value,
          createdAt: this.genericDocsCreatedAtFilter.value,
        }
      );
    }

    if (this.fireSafetyDocsPaginator) {
      // Push current list settings to the listsService
      this.listsService.setListSetting(
        'fireSafetyDocs',
        this.fireSafetyDocsPaginator.pageIndex.valueOf(),
        this.fireSafetyDocsPaginator.pageSize.valueOf(),
        {
          name: this.fireSafetyDocsNameFilter.value,
          description: this.fireSafetyDocsDescriptionFilter.value,
          createdAt: this.fireSafetyDocsCreatedAtFilter.value,
        }
      );
    }

    if (this.invitationPaginator) {
      // Push current list settings to the listsService
      this.listsService.setListSetting(
        'invitation',
        this.invitationPaginator.pageIndex.valueOf(),
        this.invitationPaginator.pageSize.valueOf(),
        {
          name: this.fireSafetyDocsNameFilter.value,
          description: this.fireSafetyDocsDescriptionFilter.value,
          createdAt: this.fireSafetyDocsCreatedAtFilter.value,

          companyName: this.invitationSupplierFilter.value,
          pec: this.invitationPecFilter.value,
          email: this.invitationEmailFilter.value,
          vatNumber: this.invitationVatNumberFilter.value,
          register: this.invitationRegisterFilter.value,
        }
      );
    }
  }

  /**
   * Get prices from backend
   *
   * @since 1.0.0
   */
  getPrices(): void {
    this.subscriptions.push(
      this.pricesService.getList(0, 15).subscribe((res) => {
        if (res.status) {
          this.prices.push(...res.data.items);
          this.getSubscriptions();
        }
      })
    );
  }

  /**
   * Load requested subscription data
   *
   * @since 1.0.0
   */
  getSubscriptions(): void {
    this.subscriptionsService.getList().subscribe(
      (res) => {
        if (res.status) {
          // this.archediSubscriptions = res.data.items;

          const subscriptions = res.data.items.map((item: any) => {
            return {
              value: item,
              label: item.name,
            };
          });
          this.availableSubscriptions = subscriptions.filter((element: any) => {
            return (
              element.value.key !== "QRANTINCENDIO"
            );
          });
          this.availableSubscriptionsKeys = this.availableSubscriptions.map(
            (element: any) => {
              return {
                value: element.value.key,
                label: element.value.name,
              };
            }
          );

          // Create an empty value
          const noSubscription = {
            value: "noSubscription",
            label: this.translateService.instant(
              "subscriptions.fields.noSubscription"
            ),
          };
          this.availableSubscriptionsKeys.push(noSubscription);
        }
      },
      (err) => {
        this.helperService.handleError(err);
      }
    );
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    const companySubscription = this.company.get('subscription');
    if (
      companySubscription.prices &&
      Array.isArray(companySubscription.prices) &&
      companySubscription.prices.length === 0 &&
      this.prices && 
      Array.isArray(this.prices)
    ) {
      for (const price of this.prices) {
        companySubscription.prices.push({
          key: price.key,
          price: price.fee,
          discount: 0
        })  
      }
    }
    if (typeof companySubscription.privacy === "undefined") {
      companySubscription.privacy = false;
    }
    if (typeof companySubscription.privacySag === "undefined") {
      companySubscription.privacySag = false;
    }
    // NFC TAG fee was introduced after NFC access: companies saved before it have no
    // value, which leaves the field required-but-empty when NFC access is active
    if (
      companySubscription.nfcAccessTagFee === undefined ||
      companySubscription.nfcAccessTagFee === null
    ) {
      companySubscription.nfcAccessTagFee = 0;
    }
    // Additional services: companies saved before they were split have neither the
    // flags nor the fees, so they inherit the legacy "additionalServices" flag and its
    // single fee; saving the contract stores them explicitly (lazy migration)
    for (const service of ADDITIONAL_SERVICE_FLAGS) {
      const feeKey = `${service}Fee`;
      companySubscription[feeKey] = additionalServiceFee(companySubscription, service);
      companySubscription[service] = isAdditionalServiceActive(companySubscription, service);
    }

    // Set model
    this.model = {
      name: this.company.get('name'),
      types: this.company.get('types'),
      vatNumber: this.company.get('vatNumber'),
      taxCode: this.company.get('taxCode'),
      email: this.company.get('email'),
      pec: this.company.get('pec'),
      sdiCode: this.company.get('sdiCode'),
      legalAddress: this.company.get('legalAddress'),
      operativeAddress: this.company.get('operativeAddress'),
      phone: this.company.get('phone'),
      webSiteUrl: this.company.get('webSiteUrl'),
      legalName: this.company.get('legalName'),
      legalLastName: this.company.get('legalLastName'),
      legalTaxCode: this.company.get('legalTaxCode'),
      legalEmail: this.company.get('legalEmail'),
      legalResidentialAddress: this.company.get('legalResidentialAddress'),
      subscription: companySubscription, // this.company.get('subscription'),
    }
    this.modelSC = {
      subscription: companySubscription, // this.company.get('subscription'),
    }

    // For price list sorting
    let index = 0;

    const categoriesOptions: any[] = [];
    const rawCategories = this.translateService.instant("companies.categories");
    for (const trans of Object.keys(rawCategories)) {
      categoriesOptions.push({
        label: rawCategories[trans],
        value: trans,
      });
    }

    // Set fields
    this.anagraphicFields = [
      // ...this.anagraphicFields,
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "name",
            templateOptions: {
              label: this.translateService.instant("companies.fields.name"),
              placeholder: this.translateService.instant(
                "companies.fields.name"
              ),
              required: true,
              minLength: 2,
              maxLength: 100,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "select",
            key: "types",
            templateOptions: {
              label: this.translateService.instant("companies.fields.types"),
              placeholder: this.translateService.instant(
                "companies.fields.types"
              ),
              required: true,
              multiple: true,
              options: [
                {
                  value: "owner",
                  label: this.translateService.instant("companies.types.owner"),
                },
                {
                  value: "manager",
                  label: this.translateService.instant(
                    "companies.types.manager"
                  ),
                },
              ],
            },
          },
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "vatNumber",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.vatNumber"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.vatNumber"
              ),
              required: false,
              minLength: 0,
              maxLength: 20,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "taxCode",
            templateOptions: {
              label: this.translateService.instant("companies.fields.taxCode"),
              placeholder: this.translateService.instant(
                "companies.fields.taxCode"
              ),
              required: false,
              minLength: 0,
              maxLength: 20,
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "email",
            templateOptions: {
              label: this.translateService.instant("companies.fields.email"),
              placeholder: this.translateService.instant(
                "companies.fields.email"
              ),
              required: true,
              minLength: 5,
              maxLength: 320,
              type: "email",
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "pec",
            templateOptions: {
              label: this.translateService.instant("companies.fields.pec"),
              placeholder: this.translateService.instant(
                "companies.fields.pec"
              ),
              required: false,
              minLength: 5,
              maxLength: 320,
              type: "email",
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "sdiCode",
            templateOptions: {
              label: this.translateService.instant("companies.fields.sdiCode"),
              placeholder: this.translateService.instant(
                "companies.fields.sdiCode"
              ),
              required: false,
              minLength: 6,
              maxLength: 7,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "address",
            key: "legalAddress",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.legalAddress"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.legalAddress"
              ),
              required: true,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "address",
            key: "operativeAddress",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.operativeAddress"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.operativeAddress"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "phone",
            templateOptions: {
              label: this.translateService.instant("companies.fields.phone"),
              placeholder: this.translateService.instant(
                "companies.fields.phone"
              ),
              required: true,
              minLength: 1,
              maxLength: 15,
              pattern: this.configService.phonePattern,
            },
          },
          {
            className: "col-12 col-md-8",
            type: "input",
            key: "webSiteUrl",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.webSiteUrl"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.webSiteUrl"
              ),
              required: false,
              minLength: 1,
              maxLength: 100,
              pattern: this.configService.webSitePattern,
            },
          },
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.legalRepresentative"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "legalName",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.legalName"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.legalName"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "legalLastName",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.legalLastName"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.legalLastName"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "legalTaxCode",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.taxCode"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.taxCode"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "address",
            key: "legalResidentialAddress",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.legalResidentialAddress"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.legalResidentialAddress"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "legalEmail",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.legalEmail"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.legalEmail"
              ),
              required: false,
              minLength: 5,
              maxLength: 320,
              type: "email",
            },
          },
        ],
      },
    ];

    this.contractFields = [
      // 1. PLATFORM SUBSCRIPTION
      {
        fieldGroupClassName: "row",
        hooks: {
          onInit: (fieldConfig) => {
            this.updateSubscriptionCodes();
          },
        },
        fieldGroup: [
          {
            className: "col-12 boxForm",
            template:
              "<div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.subscription"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.documentUpload",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.documentUpload.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.documentUpload.title"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.noticeTacitRenewalContract",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.noticeTacitRenewalContract.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.noticeTacitRenewalContract.title"
              ),
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-4",
          },  
          {
            className: "col-12 col-md-4",
            type: "select",
            key: "subscription.invoiceHolder",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.invoiceHolder.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.invoiceHolder.title"
              ),
              required: true,
              multiple: false,
              options: [
                {
                  value: "property",
                  label: this.translateService.instant(
                    "companies.fields.subscription.invoiceHolder.property"
                  ),
                },
                {
                  value: "admin",
                  label: this.translateService.instant(
                    "companies.fields.subscription.invoiceHolder.admin"
                  ),
                },
                {
                  value: "owner",
                  label: this.translateService.instant(
                    "companies.fields.subscription.invoiceHolder.owner"
                  ),
                },
              ],
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.documentUpload",
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit || !this.modelSC.subscription.documentUpload;
              },
            },
          },
          {
            className: "col-0 col-md-8",
          },
          {
            className: "col-4",
            type: "select",
            key: "subscription.key",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.subscriptionType"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.subscriptionType"
              ),
              multiple: false,
              options: this.availableSubscriptionsKeys,
              required: true,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit || !this.modelSC.subscription.documentUpload;
              },
            },
          },
          {
            className: "col-4",
            type: "input",
            key: "subscription.fee",
            templateOptions: {
              label: this.translateService.instant("subscriptions.fields.fee"),
              placeholder: this.translateService.instant(
                "subscriptions.fields.fee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit  || !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" || this.modelSC.subscription.key === "PRIVACY"))
                );
              },
            },
          },
          {
            className: "col-0 col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "subscription.availableSpace",
            templateOptions: {
              label: this.translateService.instant(
                "subscriptions.fields.availableSpace"
              ),
              placeholder: this.translateService.instant(
                "subscriptions.fields.availableSpace"
              ),
              type: "number",
              min: 0,
              max: 1000,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit || !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" || this.modelSC.subscription.key === "PRIVACY"))
                );
              },
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "subscription.totalStorageSpaceUsed",
            templateOptions: {
              label: this.translateService.instant(
                "subscriptions.fields.totalStorageSpaceUsed"
              ),
              placeholder: this.translateService.instant(
                "subscriptions.fields.totalStorageSpaceUsed"
              ),
              type: "number",
              step: 0.001,
              min: 0,
              max: 50000,
              disabled: true,
            },
          },
          {
            className: "col-0 col-md-4",
          },
          {
            className: "col-0 col-md-6",
          },
        ],
      },
      // 2. STRUCTURE SUBSCRIPTION
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.structure.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.structure",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.structure.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.structure.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY' && model.subscription.key !== 'FREE' && model.subscription.key !== 'FREEBASIC'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" || this.modelSC.subscription.key === "PRIVACY" ||
                      this.modelSC.subscription.key === "FREE" ||
                      this.modelSC.subscription.key === "FREEBASIC"))
                );
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.structurePrice",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.structurePrice"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.structurePrice"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.structure",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" || this.modelSC.subscription.key === "PRIVACY" ||
                      this.modelSC.subscription.key === "FREE" ||
                      this.modelSC.subscription.key === "FREEBASIC" ||
                      !this.modelSC.subscription.structure))
                );
              },
            },
          },
        ],
      },
      // 3. FIRST BALANCE
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.firstBalance.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.firstPriceList",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.firstPriceList.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.firstPriceList.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" || this.modelSC.subscription.key === "PRIVACY"))
                );
              },
            },
          },
          {
            className: "col-1",
          },
        ],
      },
      // 4. SUBSEQUENT INSERTIONS
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.subsequentInsertions.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.scanInsNext",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.scanInsNext.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.scanInsNext.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload || 
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" || this.modelSC.subscription.key === "PRIVACY" ||
                      
                      this.modelSC.subscription.flatRate))
                );
              },
            },
          },
          
          {
            className: "col-10",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.flatRate",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" || this.modelSC.subscription.key === "PRIVACY" ||
                      
                      this.modelSC.subscription.scanInsNext))
                );
              },
            },
          },
          {
            className: "col-2",
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalDoc",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalDoc"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalDoc"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalPdf",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalPdf"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalPdf"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
        ],
      },
      // 5. SUBSEQUENT SCANS
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.subsequentScans.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.scanNext",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.scanNext.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.scanNext.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" || this.modelSC.subscription.key === "PRIVACY" ||
                      
                      this.modelSC.subscription.flatRateScan))
                );
              },
            },
          },
          {
            className: "col-10",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.flatRateScan",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRateScan.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRateScan.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.documentUpload ||
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" || this.modelSC.subscription.key === "PRIVACY" ||
                      
                      this.modelSC.subscription.scanNext))
                );
              },
            },
          },
          
          {
            className: "col-10",
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA4",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA4"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA4"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA3",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA3"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA3"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA2",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA2"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA2"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-1",
          },
          {
            className: "col-2",
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA1",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA1"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA1"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA0",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA0"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA0"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalOutFormat",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalOutFormat"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalOutFormat"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-1",
          },
        ],
      },
      // 6. SUBSCRIPTION OPTIONS
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsServices.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          // Each additional service has its own flag and fee (not tied to documentUpload)
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsServices.safetyVideo"
              ) +
              "</strong></div>",
          },
          ...this.additionalServiceFields(
            "safetyVideoResidents",
            "companies.fields.subscription.optionsServices.safetyVideoResidents"
          ),
          ...this.additionalServiceFields(
            "safetyVideoSuppliers",
            "companies.fields.subscription.optionsServices.safetyVideoSuppliers"
          ),
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsServices.fireSafety"
              ) +
              "</strong></div>",
          },
          ...this.additionalServiceFields(
            "fireSafety",
            "companies.fields.subscription.optionsServices.fireSafety",
            "col-md-4"
          ),
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.informativeNote"
              ) +
              "</strong></div>",
          },
          ...this.additionalServiceFields(
            "informativeNote",
            "companies.fields.subscription.informativeNote"
          ),
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsNfc.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.nfcAccess",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.nfcAccess"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.nfcAccess"
              ),
              required: false,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit 
                );
              },
            },
          },
          {
            className: "col-12 col-md-2",
            type: "input",
            key: "subscription.nfcAccessFee",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.nfcAccessFee"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.nfcAccessFee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.nfcAccess",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.nfcAccess
                );
              },
            },
          },
          {
            className: "col-12 col-md-2",
            type: "input",
            key: "subscription.nfcAccessTagFee",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.nfcAccessTagFee"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.nfcAccessTagFee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.nfcAccess",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.nfcAccess
                );
              },
            },
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.startDateNfcAccess",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.startDateNfcAccess"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.startDateNfcAccess"
              ),
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.nfcAccess",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.nfcAccess
                );
              },
            },
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.endDateNfcAccess",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.endDateNfcAccess"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.endDateNfcAccess"
              ),
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.nfcAccess",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.nfcAccess
                );
              },
            },
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsSupplierEvaluation.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.suppliers",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.suppliers"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.suppliers"
              ),
              required: false,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit 
                );
              },
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "subscription.suppliersFee",
            templateOptions: {
              label: this.translateService.instant("subscriptions.fields.fee"),
              placeholder: this.translateService.instant(
                "subscriptions.fields.fee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.suppliers",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.suppliers
                );
              },
            },
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.startDateSuppliers",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.startDateSuppliers"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.startDateSuppliers"
              ),
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.suppliers",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.suppliers
                );
              },
            },
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.endDateSuppliers",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.endDateSuppliers"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.endDateSuppliers"
              ),
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.suppliers",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  !this.modelSC.subscription.suppliers
                );
              },
            },
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.suppliersNotify",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.suppliersNotify"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.suppliersNotify"
              ),
              required: false,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit 
                );
              },
            },
          },
          {
            className: "col-md-8",
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsPrivacy.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.privacy",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.privacy"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.privacy"
              ),
              required: false,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  this.modelSC.subscription.privacySag
                );
              },
            },
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.privacySag",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.privacySag"
              ),
              required: false,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  this.modelSC.subscription.privacy
                );
              },
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "subscription.privacyFee",
            templateOptions: {
              label: this.translateService.instant("subscriptions.fields.fee"),
              placeholder: this.translateService.instant(
                "subscriptions.fields.fee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.privacy || model.subscription.privacySag",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  (!this.modelSC.subscription.privacy && !this.modelSC.subscription.privacySag)
                );
              },
            },
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.startDatePrivacy",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.startDatePrivacy"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.startDatePrivacy"
              ),
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.privacy || model.subscription.privacySag",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  (!this.modelSC.subscription.privacy && !this.modelSC.subscription.privacySag)
                );
              },
            },
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.endDatePrivacy",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.endDatePrivacy"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.endDatePrivacy"
              ),
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.privacy || model.subscription.privacySag",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  (!this.modelSC.subscription.privacy && !this.modelSC.subscription.privacySag)
                );
              },
            },
          },
        ],
      },
      // 7. PRICE LIST
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.priceList.title"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12",
            template: "<hr />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12",
            template: "<hr />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return !this.enableEdit;
              },
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12",
            template: "<hr />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                "companies.fields.subscription.additionalStorage.sectionTitle"
              ) +
              "</div>",
          },
          {
            className: "col-3",
            type: "input",
            key: "subscription.extraSpaceFee",
            templateOptions: {
              label: this.translateService.instant(
                "subscriptions.fields.extraSpaceFee"
              ),
              placeholder: this.translateService.instant(
                "subscriptions.fields.extraSpaceFee"
              ),
              type: "number",
              min: 0,
              max: 500,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled": (): boolean => {
                return (
                  !this.enableEdit ||
                  (this.modelSC.subscription.key &&
                    (this.modelSC.subscription.key === "noSubscription" || this.modelSC.subscription.key === "PRIVACY"))
                );
              },
            },
          },
        ],
      },
      // // Notes
      // {
      //   fieldGroupClassName: "row",
      //   fieldGroup: [
      //     {
      //       className: "col-12",
      //       template:
      //         "<hr /><div><strong>" +
      //         this.translateService.instant(
      //           "companies.fields.subscription.notes"
      //         ) +
      //         "</strong></div>",
      //     },
      //     {
      //       className: "col-12",
      //       type: "textarea",
      //       key: "subscription.notes",
      //       templateOptions: {
      //         rows: 5,
      //       },
      //       expressionProperties: {
      //         "templateOptions.disabled": (): boolean => {
      //           return !this.enableEdit;
      //         },
      //       },
      //     },
      //   ],
      // },
    ];
  }

  /**
   * Refresh the "service active" badges shown to Admin / Operator on each
   * section: a service is active when its contract flag is saved as selected
   *
   * @since 3.0.0
   *
   * @param subscription Saved contractual conditions of the company
   */
  setActiveServices(subscription: SubscriptionRecord): void {
    const saved = subscription || {};
    this.activeServices = {
      suppliers: saved.suppliers === true,
      safetyVideoSuppliers: isAdditionalServiceActive(saved, "safetyVideoSuppliers"),
      safetyVideoResidents: isAdditionalServiceActive(saved, "safetyVideoResidents"),
      fireSafety: isAdditionalServiceActive(saved, "fireSafety"),
      informativeNote: isAdditionalServiceActive(saved, "informativeNote"),
      privacy: saved.privacy === true || saved.privacySag === true,
    };
  }

  /**
   * Build the "flag + fee" row of a single additional service.
   * The fee is not shown: its control stays in the form (hidden, not required)
   * so the stored value is kept untouched on save.
   *
   * @since 3.0.0
   *
   * @param flag Subscription flag key (its fee is stored in `${flag}Fee`)
   * @param labelKey Translation key of the checkbox label
   * @param widthClass Bootstrap width of the checkbox from md up: `col-md-auto` keeps
   *   it as wide as its label, a fixed column lets a long label wrap
   * @returns Formly fields of the row (checkbox, hidden fee): consecutive services share a line
   */
  additionalServiceFields(
    flag: string,
    labelKey: string,
    widthClass: string = "col-md-auto"
  ): FormlyFieldConfig[] {
    return [
      {
        className: `col-12 ${widthClass}`,
        type: "checkbox",
        key: `subscription.${flag}`,
        templateOptions: {
          label: this.translateService.instant(labelKey),
          required: false,
        },
        expressionProperties: {
          "templateOptions.disabled": (): boolean => !this.enableEdit,
        },
      },
      {
        className: "d-none",
        type: "input",
        key: `subscription.${flag}Fee`,
        templateOptions: {
          label: this.translateService.instant("subscriptions.fields.fee"),
          placeholder: this.translateService.instant("subscriptions.fields.fee"),
          type: "number",
          step: 0.1,
          min: 0,
          max: 10000,
        },
        expressionProperties: {
          "templateOptions.disabled": (): boolean =>
            !this.enableEdit || !this.modelSC.subscription[flag],
        },
      },
    ];
  }

  /**
   * Explain why a change of a linked service flag was refused (and reverted)
   *
   * @since 3.0.0
   *
   * @param refusal Reason of the refusal
   */
  onServiceLinkRefused(refusal: ServiceLinkRefusal): void {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("companies.dialog.serviceLinks.title"),
        message: this.translateService.instant(`companies.dialog.serviceLinks.${refusal}`),
        btnCancelText: this.translateService.instant("common.cta.close"),
        btnCancelColor: "primary",
        messageOnly: true,
      },
      width: "500px",
    });
  }

  /**
   * Autofill subscription values based on subscription type changes
   *
   * @since 1.0.0
   */
  updateSubscriptionCodes(): void {
    const subscriptionControl = this.formSC.controls.subscription;

    // Control fields
    const keyControl = this.formSC.controls.subscription.get("key");
    const startDateControl = this.formSC.controls.subscription.get("startDate");
    const suppliersControl = this.formSC.controls.subscription.get(
      "suppliers"
    );
    const nfcAccessControl = this.formSC.controls.subscription.get("nfcAccess");
    const startDateNfcAccessControl = this.formSC.controls.subscription.get(
      "startDateNfcAccess"
    );
    const startDateSuppliersControl =
      this.formSC.controls.subscription.get("startDateSuppliers");
    
    const privacyControl = this.formSC.controls.subscription.get(
      "privacy"
    );
    const privacySagControl = this.formSC.controls.subscription.get(
      "privacySag"
    );
    const startDatePrivacyControl =
      this.formSC.controls.subscription.get("startDatePrivacy");

    // Value fields
    const feeField = subscriptionControl.get("fee");
    const startDateField = subscriptionControl.get("startDate");
    const endDateField = subscriptionControl.get("endDate");
    const availableSpaceField = subscriptionControl.get("availableSpace");
    const structureField = subscriptionControl.get("structure");
    const structurePriceField = subscriptionControl.get("structurePrice");
    const firstPriceListField = subscriptionControl.get("firstPriceList");
    const scanInsNextField = subscriptionControl.get("scanInsNext");
    const flatRateField = subscriptionControl.get("flatRate");
    const scanNextField = subscriptionControl.get("scanNext");
    const flatRateScanField = subscriptionControl.get("flatRateScan");
    const startDateSuppliersField = subscriptionControl.get("startDateSuppliers");
    const endDateSuppliersField = subscriptionControl.get("endDateSuppliers");
    const nfcAccessField = subscriptionControl.get("nfcAccess");
    const startDateNfcAccessField = subscriptionControl.get(
      "startDateNfcAccess"
    );
    const endDateNfcAccessField = subscriptionControl.get("endDateNfcAccess");
    const extraSpaceFeeField = subscriptionControl.get("extraSpaceFee");
    const startDatePrivacyField = subscriptionControl.get("startDatePrivacy");
    const endDatePrivacyField = subscriptionControl.get("endDatePrivacy");

    if (keyControl) {
      keyControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(keyControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              return;
            }

            let index = -1;
            switch (res) {
              case "noSubscription":
              case "PRIVACY":
                feeField.setValue(0);
                availableSpaceField.setValue(0);
                extraSpaceFeeField.setValue(0);
                structurePriceField.setValue(0);
                structureField.setValue(false);
                firstPriceListField.setValue(false);
                scanInsNextField.setValue(false);
                flatRateField.setValue(false);
                scanNextField.setValue(false);
                flatRateScanField.setValue(false);
                nfcAccessField.setValue(false);
                break;
              case "FULL":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FULL";
                });
                 structureField.setValue(true);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(false);
                flatRateField.setValue(true);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
              case "FULLBASIC":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FULLBASIC";
                });
                structureField.setValue(true);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(true);
                flatRateField.setValue(false);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
              case "FREE":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FREE";
                });
                structureField.setValue(false);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(false);
                flatRateField.setValue(true);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
              case "FREEBASIC":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FREEBASIC";
                });
                structureField.setValue(false);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(true);
                flatRateField.setValue(false);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
            }
            if (index > -1) {
              feeField.setValue(this.archediSubscriptions[index].fee);
              availableSpaceField.setValue(
                this.archediSubscriptions[index].availableSpace
              );
              extraSpaceFeeField.setValue(
                this.archediSubscriptions[index].extraSpaceFee
              );
              structurePriceField.setValue(
                this.archediSubscriptions[index].structurePrice
              );
            }

            // Backend requires a billing code whenever the matching flag is enabled.
            // Only one code is currently defined per list, so it is used directly
            // rather than filtered by plan (their "keys" arrays are inconsistently
            // maintained and do not cover every plan that sets these flags, e.g. FULLBASIC).
            const scanInsNextCode = this.configService.availableInsCodesNext[0];
            const flatRateCode = this.configService.availableInsCodesNextForfait[0];
            const scanNextCode = this.configService.availableScanCodesNext[0];
            const flatRateScanCode = this.configService.availableScanCodesNextForfait[0];
            this.modelSC.subscription = this.modelSC.subscription || {};
            this.modelSC.subscription.scanInsNextCode =
              scanInsNextField && scanInsNextField.value && scanInsNextCode
                ? scanInsNextCode.value
                : "INSSUCC001";
            this.modelSC.subscription.flatRateCode =
              flatRateField && flatRateField.value && flatRateCode
                ? flatRateCode.value
                : "FINSSUCC001";
            this.modelSC.subscription.scanNextCode =
              scanNextField && scanNextField.value && scanNextCode
                ? scanNextCode.value
                : "SCASUCC001";
            this.modelSC.subscription.flatRateScanCode =
              flatRateScanField && flatRateScanField.value && flatRateScanCode
                ? flatRateScanCode.value
                : "FSCASUCC001";
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    // Links between services ("Nota informativa", "Valutazione fornitori"): turning a
    // service on selects the ones it requires, which cannot be turned off while it is on
    this.subscriptions.push(
      ...bindServiceLinks(
        subscriptionControl,
        (refusal) => this.onServiceLinkRefused(refusal),
        (err) => this.helperService.handleError(err)
      )
    );

    if (suppliersControl) {
      suppliersControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(suppliersControl.value))
        .subscribe(
          (res) => {
            this.suppliers = res;
            if (this.suppliers) {
              this.hiddenCardInvitation = true;
              this.hiddenCardSuppliers = false;
            } else if (this.nfcAccess) {
              this.hiddenCardInvitation = false;
              this.hiddenCardSuppliers = true;
            }

            if (!res) {
              startDateSuppliersField.setValue(null);
              endDateSuppliersField.setValue(null);
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    if (startDateControl) {
      startDateControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(startDateControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              return;
            }
            if (res && startDateField.dirty) {
              // Set endDate automatically to startDate + 1 year
              const start = new Date(res);
              const end = start;
              end.setFullYear(start.getFullYear() + 1);
              endDateField.setValue(end);

            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    if (startDateNfcAccessControl) {
      startDateNfcAccessControl.valueChanges
        .pipe(
          distinctUntilChanged(),
          startWith(startDateNfcAccessControl.value)
        )
        .subscribe(
          (res) => {
            if (!res) {
              return;
            }
            if (res && startDateNfcAccessField.dirty) {
              // Set endDate automatically to startDate + 1 year
              const start = new Date(res);
              const end = start;
              end.setDate(start.getDate() - 1);
              end.setFullYear(start.getFullYear() + 1);
              endDateNfcAccessField.setValue(end);
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    if (startDateSuppliersControl) {
      startDateSuppliersControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(startDateSuppliersControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              return;
            }
            if (res && startDateSuppliersField.dirty) {
              // Set endDate automatically to startDate + 1 year
              const start = new Date(res);
              const end = start;
              end.setDate(start.getDate() - 1);
              end.setFullYear(end.getFullYear() + 1);
              endDateSuppliersField.setValue(end);
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    // if (privacyControl) {
    //   privacyControl.valueChanges
    //     .pipe(distinctUntilChanged(), startWith(privacyControl.value))
    //     .subscribe(
    //       (res) => {
    //         if (!res) {
    //           if (startDatePrivacyField !== null && endDatePrivacyField !== null) {
    //             startDatePrivacyField.setValue(null);
    //             endDatePrivacyField.setValue(null);
    //           }
    //         }
    //       },
    //       (err) => {
    //         this.helperService.handleError(err);
    //       }
    //     );
    // }
    // if (privacySagControl) {
    //   privacySagControl.valueChanges
    //     .pipe(distinctUntilChanged(), startWith(privacySagControl.value))
    //     .subscribe(
    //       (res) => {
    //         if (!res) {
    //           if (startDatePrivacyField !== null && endDatePrivacyField !== null) {
    //             startDatePrivacyField.setValue(null);
    //             endDatePrivacyField.setValue(null);
    //           }
    //         }
    //       },
    //       (err) => {
    //         this.helperService.handleError(err);
    //       }
    //     );
    // }

    if (startDatePrivacyControl) {
      startDatePrivacyControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(startDatePrivacyControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              return;
            }
            if (res && startDatePrivacyField !== null && startDatePrivacyField.dirty && endDatePrivacyField !== null) {
              // Set endDate automatically to startDate + 1 year
              const start = new Date(res);
              const end = start;
              end.setDate(start.getDate() - 1);
              end.setFullYear(end.getFullYear() + 1);
              endDatePrivacyField.setValue(end);
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    if (nfcAccessControl) {
      nfcAccessControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(nfcAccessControl.value))
        .subscribe(
          (res) => {
            this.nfcAccess = res;
            if (this.suppliers) {
              this.hiddenCardInvitation = true;
              this.hiddenCardSuppliers = false;
            } else if (this.nfcAccess) {
              this.hiddenCardInvitation = false;
              this.hiddenCardSuppliers = true;
            }

            if (!res) {
              let index = this.suppliersDisplayedColumns.findIndex(
                (el: string) => el === 'checkbox'
              );
              if (index > -1) {
                this.suppliersDisplayedColumns.splice(index, 1);
              }
              index = this.suppliersDisplayedColumns.findIndex(
                (el: string) => el === 'register'
              );
              if (index > -1) {
                this.suppliersDisplayedColumns.splice(index, 1);
              }
              index = this.suppliersDisplayedColumns.findIndex(
                (el: string) => el === 'invitationDate'
              );
              if (index > -1) {
                this.suppliersDisplayedColumns.splice(index, 1);
              }
            } else {
              let index = this.suppliersDisplayedColumns.findIndex(
                (el: string) => el === 'checkbox'
              );
              if (index === -1) {
                this.suppliersDisplayedColumns.splice(1, 0, 'checkbox');
              }
              index = this.suppliersDisplayedColumns.findIndex(
                (el: string) => el === 'invitationDate'
              );
              if (index === -1) {
                this.suppliersDisplayedColumns.splice(10, 0, 'invitationDate');
              }
              index = this.suppliersDisplayedColumns.findIndex(
                (el: string) => el === 'register'
              );
              if (index === -1) {
                this.suppliersDisplayedColumns.splice(11, 0, 'register');
              }
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    // if (endDateControl) {
    //   endDateControl.valueChanges
    //     .pipe(distinctUntilChanged(), startWith(endDateControl.value))
    //     .subscribe(
    //       (res) => {
    //         if (!res) {
    //           return;
    //         }
    //         if (res && endDateField.dirty) {
    //           // Set startDate automatically to endDate - 1 year
    //           const end = new Date(res);
    //           const start = end;

    //           start.setFullYear(end.getFullYear() - 1);
    //           startDateField.setValue(start);

    //           if (startDateQrSafetyField) {
    //             const date = startDateField.value;
    //             startDateQrSafetyField.setValue(date);
    //           }
    //           if (endDateQrSafetyField) {
    //             const date = endDateField.value;
    //             endDateQrSafetyField.setValue(date);
    //           }
    //           if (startDateNfcAccessField) {
    //             const date = startDateField.value;
    //             startDateNfcAccessField.setValue(date);
    //           }
    //           if (endDateNfcAccessField) {
    //             const date = endDateField.value;
    //             endDateNfcAccessField.setValue(date);
    //           }
    //         }
    //       },
    //       (err) => {
    //         this.helperService.handleError(err);
    //       }
    //     );
    // }
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(formNumber: number): void {
    // Prevent invalid form submit
    if (formNumber === 1) {
      if (this.form.invalid) {
        // markAllAsTouched reaches the nested groups formly builds for dotted keys
        this.form.markAllAsTouched();
        console.warn("form", this.form);
        this.toastrService.error(
          this.translateService.instant("common.errors.form_is_invalid"),
          this.translateService.instant("common.words.error")
        );
        return;
      }
    }
    if (formNumber === 2) {
      if (this.form.invalid || this.formSC.invalid) {
        // markAllAsTouched reaches the nested groups formly builds for dotted keys
        this.form.markAllAsTouched();
        this.formSC.markAllAsTouched();
        console.warn(this.form);
        console.warn(this.formSC);
        this.toastrService.error(
          this.translateService.instant("common.errors.form_is_invalid"),
          this.translateService.instant("common.words.error")
        );
        return;
      }
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant("companies.dialog.saveMessage"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        // Activate submit button loading status
        this.formBtnOptions.active = true;

        // // Make a object copy
        // let model: any = {};
        // model = JSON.parse(JSON.stringify(companyModel));
        if (this.model.pec === '') {
          delete this.model.pec;
        }

        if (formNumber === 1) {
          // Remove subscription from model if company is not of "manager" type
          if (this.model && this.model.types && !this.model.types.includes("manager")) {
            if (this.model.subscription) {
              delete this.model.subscription;
            } 
          }
        }
        if (formNumber === 2) {
          // Remove subscription from model if company is not of "manager" type
          if (this.model && this.model.types && !this.model.types.includes("manager")) {
            if (this.model.subscription) {
              delete this.model.subscription;
            }
          } else {
            this.model.subscription = this.modelSC.subscription;

            this.nfcAccess = this.model.subscription.nfcAccess;
          }
        }

        delete this.model.requiredFields;

        this.subscriptions.push(
          this.companiesService
            .updateFields(this.company.getId(), this.model)
            .pipe(finalize(() => (this.formBtnOptions.active = false)))
            .subscribe(
              (res) => {
                if (res.status) {
                  this.toastrService.success(res.message);
                  this.enableEdit = false;
                  if (formNumber === 2) {
                    this.setActiveServices(this.model.subscription);
                  }
                }
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                // this.helperService.handleError(err);
                this.helperService.handleFormlyError(this.form, err);
              }
            )
        );
      });
  }

  removeIdsFromPrices(prices: any) {
    prices.forEach((price: any) => {
      if (price && price._id) {
        delete price._id;
      }
    });
    this.company.set("subscription.prices", prices);
  }

  /**
   * Init subUsers data table
   *
   * @since 1.0.0
   */
  initSubUsersDataTable(): void {
    if (!this.company || !this.company.getId()) {
      return;
    }
    if (!this.subUsersPaginator) {
      return;
    }
    if (!this.subUsersSort) {
      return;
    }
    // Load first set of data
    const start =
      this.subUsersPaginator.pageIndex * this.subUsersPaginator.pageSize;
    const length = this.subUsersPaginator.pageSize;
    this.subUsersDataSource.loadItems(
      this.company.getId(),
      start,
      length,
      this.subUsersSort.active,
      this.subUsersSort.direction
    );

    // Reset pagination after sorting
    this.subscriptions.push(
      this.subUsersSort.sortChange.subscribe(
        () => (this.subUsersPaginator.pageIndex = 0)
      )
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.subUsersSort.sortChange, this.subUsersPaginator.page)
        .pipe(tap(() => this.loadSubUsersPage()))
        .subscribe()
    );

    // Handle user's page change subUser
    // loading requested data
    this.subscriptions.push(
      this.subUsersPaginator.page
        .pipe(tap(() => this.loadSubUsersPage()))
        .subscribe()
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadSubUsersPage(): void {
    const start =
      this.subUsersPaginator.pageIndex * this.subUsersPaginator.pageSize;
    const length = this.subUsersPaginator.pageSize;
    this.subUsersDataSource.loadItems(
      this.company.getId(),
      start,
      length,
      this.subUsersSort.active,
      this.subUsersSort.direction
    );
  }

  /**
   * Open subUser add dialog
   *
   * @since 1.0.0
   */
  onAddSubUser(): void {
    if (!this.company || !this.company.getId()) {
      return;
    }

    // Open dialog
    const dialogRef = this.dialog.open(AddSubUserDialogComponent, {
      minWidth: "60%",
      maxHeight: "98vh",
      data: {
        companyId: this.company.getId(),
      },
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((res) => {
        if (typeof res !== "undefined" && res.createdSubUser) {
          this.loadSubUsersPage();
          return;
        }
      })
    );
  }

  /**
   * Init subProperties data table
   *
   * @since 1.0.0
   */
  initSubPropertiesDataTable(): void {
    if (!this.company || !this.company.getId()) {
      return;
    }
    if (!this.subPropertiesPaginator) {
      return;
    }
    if (!this.subPropertiesSort) {
      return;
    }
    // Load first set of data
    const start =
      this.subPropertiesPaginator.pageIndex *
      this.subPropertiesPaginator.pageSize;
    const length = this.subPropertiesPaginator.pageSize;
    this.subPropertiesDataSource.loadItems(
      this.company.getId(),
      start,
      length,
      this.subPropertiesSort.active,
      this.subPropertiesSort.direction
    );

    // Reset pagination after sorting
    this.subscriptions.push(
      this.subPropertiesSort.sortChange.subscribe(
        () => (this.subPropertiesPaginator.pageIndex = 0)
      )
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.subPropertiesSort.sortChange, this.subPropertiesPaginator.page)
        .pipe(tap(() => this.loadSubPropertiesPage()))
        .subscribe()
    );

    // Handle user's page change subProperty
    // loading requested data
    this.subscriptions.push(
      this.subPropertiesPaginator.page
        .pipe(tap(() => this.loadSubPropertiesPage()))
        .subscribe()
    );

    // Handle server-side filtering through general search
    this.subscriptions.push(
      fromEvent(this.subPropertiesInput.nativeElement, "keyup")
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
          tap(() => {
            this.subPropertiesPaginator.pageIndex = 0; // Reset page
            this.loadSubPropertiesPage();
          })
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.adminFilter.valueChanges.pipe(startWith(null as string)),
        this.ownerFilter.valueChanges.pipe(startWith(null as string)),
        this.typeFilter.valueChanges.pipe(startWith(null as string)),
        this.nameFilter.valueChanges.pipe(startWith(null as string)),
        this.masterCodeFilter.valueChanges.pipe(startWith(null as string)),
        this.addressFilter.valueChanges.pipe(startWith(null as string)),
        this.addressCityFilter.valueChanges.pipe(startWith(null as string)),
        this.subscriptionEndDateFilter.valueChanges.pipe(
          startWith(null as string)
        ),
        this.totalDocsInsertedFilter.valueChanges.pipe(
          startWith(null as string)
        ),
        this.propertiesStatusFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged() // Eliminate duplicate values
        )
        .subscribe((res) => {
          // Handle admin filter
          if (res[0]) {
            // Since we cannot search in the same time for both admin and
            // owner, we must reset admin field. Maybe this should be handled in a better way
            this.ownerFilter.setValue(undefined, { emitEvent: false });
            this.adminIsLoading = true;
            this.getAvailableAdminsAndOwners("admin", {
              name: this.adminFilter.value,
            });
            return;
          }
          // Handle owner filter
          if (res[1]) {
            // Since we cannot search in the same time for both admin and
            // owner, we must reset admin field. Maybe this should be handled in a better way
            this.adminFilter.setValue(undefined, { emitEvent: false });
            this.ownerIsLoading = true;
            this.getAvailableAdminsAndOwners("owner", {
              general: this.ownerFilter.value,
            });
            return;
          }

          // Load page only if at least one value is not null
          if (!res.every((value) => value === null)) {
            this.loadSubPropertiesPage();
          }
        })
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadSubPropertiesPage(): void {
    const start =
      this.subPropertiesPaginator.pageIndex *
      this.subPropertiesPaginator.pageSize;
    const length = this.subPropertiesPaginator.pageSize;
    // "MAPPATURA COMPLETA" is a value of the single STATO select but the
    // backend filters it via the dedicated search[mappingConfirmedOtherActivity]
    // parameter; ApiService drops undefined params so it is absent unless picked.
    const isCompleteMappingFilter =
      this.propertiesStatusFilter.value === "mappCompl";
    const search = {
      general: this.subPropertiesInput.nativeElement.value,
      admin: this.adminFilter.value ? this.adminFilter.value._id : undefined,
      owner: this.ownerFilter.value ? this.ownerFilter.value._id : undefined,
      type: this.typeFilter.value,
      name: this.nameFilter.value,
      masterCode: this.masterCodeFilter.value,
      address: this.addressFilter.value,
      addressCity: this.addressCityFilter.value,
      subscriptionEndDate: this.subscriptionEndDateFilter.value
        ? moment(this.subscriptionEndDateFilter.value).toISOString()
        : null,
      totalDocsInserted: this.totalDocsInsertedFilter.value,
      status: isCompleteMappingFilter
        ? undefined
        : this.propertiesStatusFilter.value,
      mappingConfirmedOtherActivity: isCompleteMappingFilter ? true : undefined,
    };
    this.subPropertiesDataSource.loadItems(
      this.company.getId(),
      start,
      length,
      this.subPropertiesSort.active,
      this.subPropertiesSort.direction,
      search,
      this.loggedUser._id
    );
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToSubPropsListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe((settings: ListSetting[]) => {
        if (settings.length) {
          settings.forEach((setting: ListSetting) => {
            if (setting.name === name) {
              if (setting.pageIndex) {
                this.subPropertiesPaginator.pageIndex = setting.pageIndex;
              }
              if (setting.pageSize) {
                this.subPropertiesPaginator.pageSize = setting.pageSize;
              }
              if (setting.search && setting.search.general) {
                this.subPropertiesInput.nativeElement.value =
                  setting.search.general;
              }
              if (setting.search && setting.search.admin) {
                this.adminFilter.setValue(setting.search.admin);
              }
              if (setting.search && setting.search.owner) {
                this.ownerFilter.setValue(setting.search.owner);
              }
              if (setting.search && setting.search.type) {
                this.typeFilter.setValue(setting.search.type);
              }
              if (setting.search && setting.search.name) {
                this.nameFilter.setValue(setting.search.name);
              }
              if (setting.search && setting.search.masterCode) {
                this.masterCodeFilter.setValue(setting.search.masterCode);
              }
              if (setting.search && setting.search.address) {
                this.addressFilter.setValue(setting.search.address);
              }
              if (setting.search && setting.search.addressCity) {
                this.addressCityFilter.setValue(setting.search.addressCity);
              }
              if (setting.search && setting.search.subscriptionEndDate) {
                this.subscriptionEndDateFilter.setValue(
                  setting.search.subscriptionEndDate
                );
              }
              if (setting.search && setting.search.totalDocsInserted) {
                this.totalDocsInsertedFilter.setValue(
                  setting.search.totalDocsInserted
                );
              }
              if (setting.search && setting.search.propertiesStatusFilter) {
                this.propertiesStatusFilter.setValue(setting.search.propertiesStatusFilter);
              }
            }
          });
        }
      })
    );
  }

  /**
   * Get the list of available admins and owners
   * used to allow filtering on owner and admin columns
   *
   * @since 1.0.0
   */
  getAvailableAdminsAndOwners(type?: string, search?: any): void {
    this.subscriptions.push(
      this.companiesService
        .getList(0, 10, null, null, search)
        .pipe(
          finalize(() => {
            this.adminIsLoading = false;
            this.ownerIsLoading = false;
          })
        )
        .subscribe((res) => {
          if (res.status) {
            if (type === "admin") {
              this.availableAdmins = res.data.items;
            } else if (type === "owner") {
              this.availableOwners = res.data.items;
            } else {
              this.availableAdmins = res.data.items;
              this.availableOwners = res.data.items;
            }
          }
        })
    );
  }

  /**
   * Handle click on a selected admin in the mat-autocomplete list and fires the loadPage()
   *
   * @since 1.0.0
   */
  onAdminFilterSelected(event: MatOptionSelectionChange): void {
    const value = event.source.value;
    this.adminFilter.setValue(value, { emitEvent: false });
    this.ownerFilter.setValue(undefined, { emitEvent: false });
    this.loadSubPropertiesPage();
  }

  /**
   * Handle click on a selected owner in the mat-autocomplete list and fires the loadSubPropertiesPage()
   *
   * @since 1.0.0
   */
  onOwnerFilterSelected(event: MatOptionSelectionChange): void {
    const value = event.source.value;
    this.adminFilter.setValue(undefined, { emitEvent: false });
    this.ownerFilter.setValue(value, { emitEvent: false });
    this.loadSubPropertiesPage();
  }

  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
  displayAdminOrOwnerFn(arg?: ICompany): string | undefined {
    return arg ? `${arg.name}` : undefined;
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetSubPropertiesInput(): void {
    this.subPropertiesInput.nativeElement.value = "";
    const event = new KeyboardEvent("keyup");
    this.subPropertiesInput.nativeElement.dispatchEvent(event);
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetSubPropertiesFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting("", 0, 10, {});

    // Reset tables' filters
    this.subPropertiesInput.nativeElement.value = "";
    this.adminFilter.setValue(null);
    this.ownerFilter.setValue(null);
    this.typeFilter.setValue(null);
    this.nameFilter.setValue(null);
    this.masterCodeFilter.setValue(null);
    this.addressFilter.setValue(null);
    this.addressCityFilter.setValue(null);
    this.subscriptionEndDateFilter.setValue(null);
    this.totalDocsInsertedFilter.setValue(null);
    this.propertiesStatusFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.subPropertiesSort.direction = "desc";
    this.subPropertiesSort.active = "subscriptionEndDate";
    this.subPropertiesSort._stateChanges.next();

    // Reset paginator
    this.subPropertiesPaginator.pageIndex = 0;
    this.subPropertiesPaginator.pageSize = 10;

    // Reload table's data
    this.loadSubPropertiesPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetSubPropertiesFilter(filter: FormControl): void {
    filter.reset();
    this.loadSubPropertiesPage();
  }

  /**
   * Remove current user from database
   *
   * @since 1.0.0
   */
  onDelete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(
          "companies.detail.deleteTab.confirmDeletion.title"
        ),
        message: this.translateService.instant(
          "companies.detail.deleteTab.confirmDeletion.message"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.companiesService
            .delete(this.company.getId())
            .pipe(finalize(() => (this.deleteBtnOptions.active = false)))
            .subscribe(
              (res2) => {
                this.toastrService.success(res2.message);
                // Once the user is deleted, redirect to suspended list
                this.router.navigate([
                  this.lang,
                  "app",
                  "companies",
                  "suspended",
                ]);
                return;
              },
              (err) => {
                // Choose one of the following error handling
                // method. The first one shows a message right
                // under the form fields (if the form is properly
                // setted), the second one shows toastr
                // notifications for each error
                // this.helperService.handleFormError(this.passwordForm, err);
                this.helperService.handleError(err);
              }
            )
        );
      });
  }

  /**
   * Remove current user from database
   *
   * @since 1.0.0
   */
  onRemoveSubUser(element: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant("dialogs.confirm.message"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.companiesService
            .deleteSubUser(this.company.getId(), element.userId)
            .pipe(finalize(() => (this.deleteBtnOptions.active = false)))
            .subscribe(
              (res2) => {
                this.toastrService.success(res2.message);
                // Once the user is removed, reload data table
                this.initSubUsersDataTable();
                return;
              },
              (err) => {
                this.helperService.handleError(err);
              }
            )
        );
      });
  }

  /**
   * Trigger the 'add phonebook' dialog
   *
   * @since 1.0.0
   */
  onAddPhonebookContact(): void {
    if (!this.company || !this.company.getId()) {
      return;
    }

    // Open dialog
    const dialogRef = this.dialog.open(AddPhonebookContactDialogComponent, {
      minWidth: "60%",
      maxHeight: "98vh",
      data: {
        companyId: this.company.getId(),
      },
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe(
        (res) => {
          if (typeof res !== "undefined" && res.createdPhonebookContact) {
            this.loadPhonebookPage();
            return;
          }
        },
        (err) => {
          // this.helperService.handleError(err);
        }
      )
    );
  }

  
  /**
   * Init suppliers data table
   *
   * @since 1.0.0
   */
  initSuppliersDataTable(): void {
    // Reset pagination after sorting
    this.subscriptions.push(this.suppliersSort.sortChange.subscribe(() => this.suppliersPaginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.suppliersSort.sortChange, this.suppliersPaginator.page)
        .pipe(
          tap(() => this.loadSuppliersPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.companyNameFilter.valueChanges.pipe(startWith(null as string)),
        this.qualificationsFilter.valueChanges.pipe(startWith(null as string)),
        this.startCompanyDateFilter.valueChanges.pipe(startWith(null as string)),
        this.registrationFilter.valueChanges.pipe(startWith(null as string)),
        this.durcFilter.valueChanges.pipe(startWith(null as string)),
        this.creditLicenceFilter.valueChanges.pipe(startWith(null as string)),
        this.creditsFilter.valueChanges.pipe(startWith(null as string)),
        this.statusSuppliersFilter.valueChanges.pipe(startWith(null as string)),
        this.employeesFilter.valueChanges.pipe(startWith(null as string)),
        this.registerFilter.valueChanges.pipe(startWith(null as string)),
        this.invitationDateFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(
          res => {
            // Load page only if at least one value is not null
            if (!res.every((value) => value === null)) {
              this.loadSuppliersPage();
            }
          }
        )
    );

    // Load first set of data
    this.loadSuppliersPage();
  }
  
  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadSuppliersPage(): void {
    const start = this.suppliersPaginator.pageIndex * this.suppliersPaginator.pageSize;
    const length = this.suppliersPaginator.pageSize;
    
    const search = {
      general: '', // this.input.nativeElement.value,
      companyName: this.companyNameFilter.value,
      qualifications: this.qualificationsFilter.value,
      startCompanyDate: this.startCompanyDateFilter.value,
      registration: this.registrationFilter.value,
      durc: this.durcFilter.value,
      creditLicence: this.creditLicenceFilter.value,
      credits: this.creditsFilter.value,
      status: this.statusSuppliersFilter.value,
      employees: this.employeesFilter.value,
      register: this.registerFilter.value,
      invitationDate: this.invitationDateFilter.value,
      requestedCompany: this.company.get('_id'),
    };

    const master = true;

    this.suppliersDataSource.loadItems(start, length, this.suppliersSort.active, this.suppliersSort.direction, search, master);
  }
  
  /**
   * Export suppliers in a file with given format
   *
   * @since 1.0.0
   */
  onExportSuppliers(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('suppliers.list.exportFileName')} ${date}`;
    this.subscriptions.push(
      this.suppliersService.getExportV2('listv3', this.company.get('_id')).subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFiltersSuppliers(): void {
    // Reset lists service data
    this.listsService.setListSetting('', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.companyNameFilter.setValue(null);
    this.qualificationsFilter.setValue(null);
    this.startCompanyDateFilter.setValue(null);
    this.registrationFilter.setValue(null);
    this.durcFilter.setValue(null);
    this.creditLicenceFilter.setValue(null);
    this.creditsFilter.setValue(null);
    this.statusSuppliersFilter.setValue(null);
    this.employeesFilter.setValue(null);
    this.registerFilter.setValue(null);
    this.invitationDateFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.suppliersSort.direction = 'desc';
    this.suppliersSort.active = 'companyName';
    this.suppliersSort._stateChanges.next();

    // Reset paginator
    this.suppliersPaginator.pageIndex = 0;
    this.suppliersPaginator.pageSize = 10;

    // Reload table's data
    this.loadSuppliersPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetSuppliersFilter(filter: FormControl): void {
    filter.reset();
    this.loadSuppliersPage();
  }

  
  /**
   * open pop up info of Qualifications
   *
   * @since 1.0.0
   */
  onInfoPopUp(): void {
    const width = "600px";
    const title = this.translateService.instant('suppliers.fields.supplierDocuments.qualifications');
    let message = `${this.translateService.instant('suppliers.qualifications.qualificationA.description')} <br><br>`;
    message = `${message} ${this.translateService.instant('suppliers.qualifications.qualificationB.description')} <br><br>`
    message = `${message} ${this.translateService.instant('suppliers.qualifications.qualificationC.description')} <br><br>`
    message = `${message} ${this.translateService.instant('suppliers.qualifications.qualificationD.description')} <br><br>`
    message = `${message} ${this.translateService.instant('suppliers.qualifications.qualificationE.description')} <br><br>`
    message = `${message} ${this.translateService.instant('suppliers.qualifications.qualificationF.description')} <br><br>`
    message = `${message} ${this.translateService.instant('suppliers.qualifications.qualificationG.description')} <br><br>`
    if (title.trim() !== '') {
      this.dialog.open(InfoDialogComponent, {
        data: { 
          title,
          message,
        },
        width,
        maxWidth: width,
      });
    }
  }

  /**
   * Handle delete supplier to Master list of supplier
   */
  onDeleteAssociation(element: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("suppliers.dialog.deleteAssociation.title"),
        message: this.translateService.instant(
          "suppliers.dialog.deleteAssociation.message"
        ),
        btnOkText: this.translateService.instant("common.cta.ok"),
        btnCancelText: this.translateService.instant("common.cta.close"),
      },
      width: "500px",
      disableClose: true,
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
        this.suppliersService.deleteSupplierAssociation(this.company.get('_id'), element._id).subscribe(
          res => {
            if (res.status){
              this.loadSuppliersPage();
            } else {
              this.helperService.handleError(res.message);
            }
          },
          err => {
            this.helperService.handleError(err);
          }
        );
        }
      });
  }

  /**
   * Init subUsers data table
   *
   * @since 1.0.0
   */
  initPhonebookDataTable(): void {
    if (!this.company || !this.company.getId()) {
      return;
    }
    if (!this.phonebookPaginator || !this.phonebookSort) {
      return;
    }

    // Reset pagination after sorting
    this.subscriptions.push(
      this.phonebookSort.sortChange.subscribe(
        () => (this.phonebookPaginator.pageIndex = 0)
      )
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.phonebookSort.sortChange, this.phonebookPaginator.page)
        .pipe(tap(() => this.loadPhonebookPage()))
        .subscribe()
    );

    // Handle user's page change subUser
    // loading requested data
    this.subscriptions.push(
      this.phonebookPaginator.page
        .pipe(tap(() => this.loadPhonebookPage()))
        .subscribe()
    );

    // Load first set of data
    this.loadPhonebookPage();
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPhonebookPage(): void {
    const start =
      this.phonebookPaginator.pageIndex * this.phonebookPaginator.pageSize;
    const length = this.phonebookPaginator.pageSize;
    this.phonebookDataSource.loadItems(
      this.company.getId(),
      start,
      length,
      this.phonebookSort.active,
      this.phonebookSort.direction
    );
  }

  /**
   * Handle click on "Open" button near to each phonebook item
   * and open a view/edit dialog for clicked phonebook
   *
   * @since 1.0.0
   */
  onOpenPhonebookContact(element: any): void {
    if (!element) {
      return;
    }

    // Open dialog
    const dialogRef = this.dialog.open(EditPhonebookContactDialogComponent, {
      minWidth: "60%",
      maxHeight: "98vh",
      data: {
        phonebookContact: element,
        companyId: this.company.getId(),
      },
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe(
        (res) => {
          if (res) {
            this.loadPhonebookPage();
            return;
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Handle click on "Remove" button near to each phonebook item
   * and delete it
   */
  onRemovePhonebookContact(element: any): void {
    if (!element) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant("dialogs.confirm.message"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.companiesService
            .deletePhonebookContact(this.company.getId(), element._id)
            .pipe(finalize(() => (this.deleteBtnOptions.active = false)))
            .subscribe(
              (res2) => {
                this.toastrService.success(res2.message);
                // Once the user is removed, reload data table
                this.initPhonebookDataTable();
                return;
              },
              (err) => {
                this.helperService.handleError(err);
              }
            )
        );
      });
  }

  /**
   * Handle new message dialog open
   *
   * @since 1.0.0
   */
  onAddCommunication(element: any): void {
    if (!element) {
      return;
    }

    // Open dialog
    const newMessageDialogRef = this.newMessageDialog.open(
      AddCommunicationDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          recipients: [
            {
              email: element.email,
              name: `${element.firstName} ${element.lastName}`,
            },
          ],
          phonebook: this.company.get("phonebook"),
        },
        disableClose: true,
      }
    );

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    newMessageDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.toastrService.success(
          this.translateService.instant("communications.add.sendedSuccessfully")
        );
      });
  }

  /**
   * Toggle activation in all list elements
   *
   * @since 1.0.0
   */
  onToggleSelected(element: MatSlideToggleChange): void {
    if (!element.checked) {
      // Prevent invalid form submit
      if (this.form.invalid) {
        this.form.markAllAsTouched();
        this.toastrService.error(
          this.translateService.instant("common.errors.form_is_invalid")
        );
        element.source.toggle();
        return;
      }
    }
    this.enableEdit = element.checked;
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe((settings: ListSetting[]) => {
        if (settings.length) {
          settings.forEach((setting: ListSetting) => {
            if (setting.name === name) {
              if (setting.pageIndex) {
                this.balancesPaginator.pageIndex = setting.pageIndex;
              }
              if (setting.pageSize) {
                this.balancesPaginator.pageSize = setting.pageSize;
              }
              if (setting.search && setting.search.general) {
                this.balancesInput.nativeElement.value = setting.search.general;
              }
              if (setting.search && setting.search.number) {
                this.numberFilter.setValue(setting.search.number);
              }
              if (setting.search && setting.search.property) {
                this.propertyFilter.setValue(setting.search.property);
              }
              if (setting.search && setting.search.status) {
                this.statusFilter.setValue(setting.search.status);
              }
              if (setting.search && setting.search.total) {
                this.totalFilter.setValue(setting.search.total);
              }
              if (setting.search && setting.search.discount) {
                this.discountFilter.setValue(setting.search.discount);
              }
              if (setting.search && setting.search.createdAt) {
                this.createdAtFilter.setValue(setting.search.createdAt);
              }
            }
          });
        }
      })
    );
  }

  /**
   * Init balances data table
   *
   * @since 1.0.0
   */
  initBalancesDataTable(): void {
    // Load first set of data
    this.loadBalancesPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.balancesSort.sortChange.subscribe(
        () => (this.balancesPaginator.pageIndex = 0)
      )
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.balancesSort.sortChange, this.balancesPaginator.page)
        .pipe(tap(() => this.loadBalancesPage()))
        .subscribe()
    );

    // Handle server-side filtering through general search
    this.subscriptions.push(
      fromEvent(this.balancesInput.nativeElement, "keyup")
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
          tap(() => {
            this.balancesPaginator.pageIndex = 0; // Reset page
            this.loadBalancesPage();
          })
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    combineLatest([
      this.numberFilter.valueChanges.pipe(startWith(null as string)),
      this.propertyFilter.valueChanges.pipe(startWith(null as string)),
      this.statusFilter.valueChanges.pipe(startWith(null as string)),
      this.totalFilter.valueChanges.pipe(startWith(null as string)),
      this.discountFilter.valueChanges.pipe(startWith(null as string)),
      this.createdAtFilter.valueChanges.pipe(startWith(null as string)),
    ])
      .pipe(
        debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged() // Eliminate duplicate values
      )
      .subscribe((res) => {
        if (res) {
          this.propertyIsLoading = true;
          this.getAvailableProperties({
            general: this.propertyFilter.value,
          });
        }
        // Load page only if at least one value is not null
        if (!res.every((value) => value === null)) {
          this.loadBalancesPage();
        }
      });
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadBalancesPage(): void {
    const start =
      this.balancesPaginator.pageIndex * this.balancesPaginator.pageSize;
    const length = this.balancesPaginator.pageSize;
    const search = {
      general: this.balancesInput.nativeElement.value,
      number: this.numberFilter.value,
      property: this.propertyFilter.value
        ? this.propertyFilter.value._id
        : undefined,
      status: this.statusFilter.value,
      total: this.totalFilter.value,
      discount: this.discountFilter.value,
      createdAt: this.createdAtFilter.value
        ? new Date(this.createdAtFilter.value).toISOString()
        : null,
    };
    this.balancesDataSource.loadCompanyItems(
      this.company.getId(),
      start,
      length,
      this.balancesSort.active,
      this.balancesSort.direction,
      search
    );
  }

  /**
   * Export properties in a file with given format
   *
   * @since 1.0.0
   */
  onExport(target: string, propertyId: string): void {
    if (target && typeof propertyId !== "undefined") {
      // Handle export for missing/inserted documents
      const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
      const fileName = `${this.translateService.instant(
        `properties.list.${target}`
      )} ${date}`;
      this.subscriptions.push(
        this.propertiesService.getDocExport(target, propertyId).subscribe(
          (res) => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: "application/vnd.ms-excel",
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          (err) => {
            this.helperService.handleError(err);
          }
        )
      );
    }
  }

  /**
   * Export subProperties in a file with given format
   *
   * @since 1.0.0
   */
  onExportSubProperties(): void {
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      "properties.list.exportFileName"
    )} ${date}`;
    this.subscriptions.push(
      this.propertiesService.getExport(this.company.getId()).subscribe(
        (res) => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: "application/vnd.ms-excel",
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Export balances in a file with given format
   *
   * @since 1.0.0
   */
  onExportBalances(): void {
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      "balances.list.exportFileName"
    )} ${date}`;
    this.subscriptions.push(
      this.balancesService.getExportCompany(this.company.getId()).subscribe(
        (res) => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: "application/vnd.ms-excel",
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting("balances", 0, 10, {});

    // Reset tables' filters
    this.balancesInput.nativeElement.value = "";
    this.numberFilter.setValue(null);
    this.propertyFilter.setValue(null);
    this.statusFilter.setValue(null);
    this.totalFilter.setValue(null);
    this.discountFilter.setValue(null);
    this.createdAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.balancesSort.direction = "desc";
    this.balancesSort.active = "createdAt";
    this.balancesSort._stateChanges.next();

    // Reset balancesPaginator
    this.balancesPaginator.pageIndex = 0;
    this.balancesPaginator.pageSize = 10;

    // Reload table's data
    this.loadBalancesPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadBalancesPage();
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    this.balancesInput.nativeElement.value = "";
    const event = new KeyboardEvent("keyup");
    this.balancesInput.nativeElement.dispatchEvent(event);
  }

  /**
   * Reactivate current user
   *
   * @since 1.0.0
   */
  onReactivate(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(
          "companies.detail.deleteTab.confirmActivation.title"
        ),
        message: this.translateService.instant(
          "companies.detail.deleteTab.confirmActivation.message"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.companiesService
            .activate(this.company.getId())
            .pipe(finalize(() => (this.reactivateBtnOptions.active = false)))
            .subscribe(
              (res2) => {
                this.toastrService.success(res2.message);
                // Once the user is deleted, redirect to list
                this.router.navigate([this.lang, "app", "companies", "list"]);
                return;
              },
              (err) => {
                // Choose one of the following error handling
                // method. The first one shows a message right
                // under the form fields (if the form is properly
                // setted), the second one shows toastr
                // notifications for each error
                // this.helperService.handleFormError(this.passwordForm, err);
                this.helperService.handleError(err);
              }
            )
        );
      });
  }

  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
  displayFn(arg?: PropertyInterface): string | undefined {
    return arg ? `${arg.name}` : undefined;
  }

  /**
   * Handle click on a selected property in the mat-autocomplete list and fires the loadPage()
   *
   * @since 1.0.0
   */
  onPropertyFilterSelected(event: MatOptionSelectionChange): void {
    const value = event.source.value;
    // this.propertyFilter.setValue(value, { emitEvent: false });
  }

  /**
   * Get the list of available admins and owners
   * used to allow filtering on owner and admin columns
   *
   * @since 1.0.0
   */
  getAvailableProperties(search?: any): void {
    this.subscriptions.push(
      this.companiesService
        .getSubPropertiesList(this.company.getId(), 0, null, null, null, search)
        .pipe(
          finalize(() => {
            this.propertyIsLoading = false;
          })
        )
        .subscribe((res) => {
          if (res.status) {
            if (res.data.items && Array.isArray(res.data.items)) {
              this.availableProperties = [];
              res.data.items.forEach((item: any) => {
                item["_id"] = item.propertyId;
                this.availableProperties.push(item);
              });
            }
            this.availableProperties = res.data.items;
          }
        })
    );
  }

  
  /**
   * Init subUsers data table
   *
   * @since 1.0.0
   */
  initAccessListDataTable(): void {
    // Load first set of data
    this.loadPageAccessList();

    // Reset pagination after sorting
    this.subscriptions.push(this.accessListSort.sortChange.subscribe(() => this.accessListPaginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.accessListSort.sortChange, this.accessListPaginator.page)
        .pipe(
          tap(() => this.loadPageAccessList())
        )
        .subscribe()
    );


    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.userFilter.valueChanges.pipe(startWith(null as string)),
        this.typeAccessFilter.valueChanges.pipe(startWith(null as string)),
        this.emailFilter.valueChanges.pipe(startWith(null as string)),
        this.companyFilter.valueChanges.pipe(startWith(null as string)),
        this.ipAddressFilter.valueChanges.pipe(startWith(null as string)),
        this.roleFilter.valueChanges.pipe(startWith(null as string)),
        this.createdAtAccessFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(
          res => {
            // Load page only if at least one value is not null
            if (!res.every((value) => value === null)) {
              this.loadPageAccessList();
            }
          }
        )
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPageAccessList(): void {
    const start = this.accessListPaginator.pageIndex * this.accessListPaginator.pageSize;
    const length = this.accessListPaginator.pageSize;
    const search = {
      general: '', // this.input.nativeElement.value,
      user: this.userFilter.value,
      type: this.typeAccessFilter.value,
      email: this.emailFilter.value,
      company: this.companyFilter.value,
      ipAddress: this.ipAddressFilter.value,
      role: this.roleFilter.value,
      createdAt: this.createdAtAccessFilter.value ? new Date(this.createdAtAccessFilter.value).toISOString() : undefined,
      requestedCompany: this.company.get('_id'),
    };
    this.accessListDataSource.loadItems(start, length, this.accessListSort.active, this.accessListSort.direction, search);
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFiltersAccessList(): void {
    // Reset lists service data
    this.listsService.setListSetting('', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.userFilter.setValue(null);
    this.typeAccessFilter.setValue(null);
    this.emailFilter.setValue(null);
    this.companyFilter.setValue(null);
    this.ipAddressFilter.setValue(null);
    this.roleFilter.setValue(null);
    this.createdAtAccessFilter.setValue(null);


    // Reset tables' sorting versus and column
    this.accessListSort.direction = 'desc';
    this.accessListSort.active = 'createdAt';
    this.accessListSort._stateChanges.next();

    // Reset paginator
    this.accessListPaginator.pageIndex = 0;
    this.accessListPaginator.pageSize = 10;

    // Reload table's data
    this.loadPageAccessList();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
   onResetFilterAccessList(filter: FormControl): void {
    filter.reset();
    this.loadPageAccessList();
  }

  /**
   * Export companies in a file with given format
   *
   * @since 1.0.0
   *
   * @param format The format. Can be 'excel' or 'csv'
   */
   onExportAccessList(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('accessLogs.list.exportFileName')} ${date}`;
    this.subscriptions.push(
      this.accessLogsService.getExport(this.company.get('_id')).subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /** 
   *  Export contract in docx
   * 
   * @since 1.0.0 
  */
  onGetContract(): void {
    this.detailCardIsLoading = true;
    PizZipUtils.getBinaryContent(
      'https://api.archedi.it/uploads/CONTRATTO-UTILIZZO-PIATTAFORMA-ARCHEDI.docx',
      (error: Error | null, content: string) => {
        try {
          if (error) {
            throw error;
          }
          const zip = new PizZip(content);
          const doc = new Docxtemplater(zip, {
            paragraphLoop: true,
            linebreaks: true,
          });
          
          doc.setData({
            company: this.companyData.name,
            legalAddress: `${this.companyData.legalAddress.route} ${this.companyData.legalAddress.streetNumber} - ${this.companyData.legalAddress.postalCode} ${this.companyData.legalAddress.city} ${this.companyData.legalAddress.countryCodeTwo}`,
            operativeAddres: this.companyData.operativeAddress ? `${this.companyData.operativeAddress.route} ${this.companyData.operativeAddress.streetNumber} - ${this.companyData.operativeAddress.postalCode} ${this.companyData.operativeAddress.city} ${this.companyData.operativeAddress.countryCodeTwo}` : `${this.companyData.legalAddress.route} ${this.companyData.legalAddress.streetNumber} - ${this.companyData.legalAddress.postalCode} ${this.companyData.legalAddress.city} ${this.companyData.legalAddress.countryCodeTwo}`,
            taxCode: this.companyData.taxCode ? this.companyData.taxCode : '……………………………',
            vatNumber: this.companyData.vatNumber ? this.companyData.vatNumber : '……………………………',
            date: moment(this.disclaimerAt).isValid() ? moment(this.disclaimerAt).format('DD-MM-YYYY hh:mm:ss'):'',
            fullName: this.disclaimerUser,
          });

          // render the document => replace all occurences
          doc.render();
          
          const out = doc.getZip().generate({
            type: 'blob',
            mimeType:
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          });
          // Output the document using Data-URI
          FileSaver.saveAs(out, 'CONTRATTO-UTILIZZO-PIATTAFORMA-ARCHEDI.docx');
          this.detailCardIsLoading = false;
        } catch (error) {
          this.detailCardIsLoading = false;
          this.helperService.handleError(JSON.stringify(error));
        }
      }
    );
  }


 
  /**
   * Init safetyVideoSuppliers data table
   *
   * @since 1.0.0
   */
  initSafetyVideoSuppliersDataTable(): void {
    // Load first set of data
    this.loadSafetyVideoSuppliersPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.safetyVideoSuppliersSort.sortChange.subscribe(
        () => (this.safetyVideoSuppliersPaginator.pageIndex = 0)
      )
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.safetyVideoSuppliersSort.sortChange, this.safetyVideoSuppliersPaginator.page)
        .pipe(tap(() => this.loadSafetyVideoSuppliersPage()))
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    combineLatest([
      this.safetyVideoSuppliersFirstNameFilter.valueChanges.pipe(startWith(null as string)),
      this.safetyVideoSuppliersLastNameFilter.valueChanges.pipe(startWith(null as string)),
      this.safetyVideoSuppliersCompanyNameFilter.valueChanges.pipe(startWith(null as string)),
      this.safetyVideoSuppliersCreatedAtFilter.valueChanges.pipe(startWith(null as string)),
    ])
      .pipe(
        debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged() // Eliminate duplicate values
      )
      .subscribe((res) => {
        // Load page only if at least one value is not null
        if (!res.every((value) => value === null)) {
          this.loadSafetyVideoSuppliersPage();
        }
      });
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadSafetyVideoSuppliersPage(): void {
    const start = this.safetyVideoSuppliersPaginator.pageIndex * this.safetyVideoSuppliersPaginator.pageSize;
    const length = this.safetyVideoSuppliersPaginator.pageSize;
    const search = {
      firstName: this.safetyVideoSuppliersFirstNameFilter.value,
      lastName: this.safetyVideoSuppliersLastNameFilter.value,
      companyName: this.safetyVideoSuppliersCompanyNameFilter.value,
      createdAt: this.safetyVideoSuppliersCreatedAtFilter.value,
    };
    this.safetyVideoSuppliersDataSource.loadSuppliersVideoItems(
      this.company.get('_id'),
      start,
      length,
      this.safetyVideoSuppliersSort.active,
      this.safetyVideoSuppliersSort.direction,
      search
    );
  }

  
  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFiltersSafetyVideoSuppliers(): void {
    // Reset lists service data
    this.listsService.setListSetting("safetyVideoSuppliers", 0, 10, {});

    // Reset tables' filters
    // this.safetyVideoSuppliersInput.nativeElement.value = "";
    this.safetyVideoSuppliersFirstNameFilter.setValue(null);
    this.safetyVideoSuppliersLastNameFilter.setValue(null);
    this.safetyVideoSuppliersCompanyNameFilter.setValue(null);
    this.safetyVideoSuppliersCreatedAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.safetyVideoSuppliersSort.direction = "asc";
    this.safetyVideoSuppliersSort.active = "createdAt";
    this.safetyVideoSuppliersSort._stateChanges.next();

    // Reset safetyVideoSuppliersPaginator
    this.safetyVideoSuppliersPaginator.pageIndex = 0;
    this.safetyVideoSuppliersPaginator.pageSize = 10;

    // Reload table's data
    this.loadSafetyVideoSuppliersPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilterSafetyVideoSuppliers(filter: FormControl): void {
    filter.reset();
    this.loadSafetyVideoSuppliersPage();
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInputSafetyVideoSuppliers(): void {
    this.safetyVideoSuppliersInput.nativeElement.value = "";
    const event = new KeyboardEvent("keyup");
    this.safetyVideoSuppliersInput.nativeElement.dispatchEvent(event);
  }
  
  /**
   * Handle click open QRcode safetyVideoSuppliers dialogbox
   *
   * @since 1.0.0
   */
  onSafetyVideoSuppliersPrintQRcode(): void {
    this.dialog.open(PrintQrcodeDialogComponent, {
      data: {
        qrCode: this.safetyVideoSuppliersQrcodeData,
        name: this.companyData.name,
        city: this.companyData.legalAddress.city,
        address: `${this.companyData.legalAddress.route} ${this.companyData.legalAddress.streetNumber}`
      },
      width: "700px",
    });
  }

  /**
   * Handle generate QRcode docx
   *
   * @since 1.0.0
   */
  onSafetyVideoSuppliersGenerateQrCodeDoc(): void {
    const qrImage = document
      .getElementById("safetyVideoSuppliersQrcodeDataImage")
      .lastElementChild.lastElementChild.getAttribute("src");

    // Create document data
    const doc = new Document();
    const image = Media.addImage(doc, qrImage, 150, 150, {
      floating: {
        horizontalPosition: {
          relative: HorizontalPositionRelativeFrom.PAGE,
          align: HorizontalPositionAlign.CENTER,
        },
        verticalPosition: {
          relative: VerticalPositionRelativeFrom.PARAGRAPH,
          align: VerticalPositionAlign.CENTER,
        },
      },
    });

    doc.addSection({
      properties: {},
      children: [
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 400 },
          children: [
            new TextRun({
              text: `AMMINISTRATORE ${this.company.get("name")}`,
              bold: true,
              allCaps: true,
              color: "auto",
              size: 24 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.TITLE,
          children: [
            new TextRun({
              text: `ATTENZIONE`,
              font: "Calibri",
              color: "FF0000",
              bold: true,
              allCaps: true,
              size: 28 * 2,
              underline: {
                color: "FF0000",
                type: UnderlineType.SINGLE,
              },
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.TITLE,
          spacing: { after: 500 },
          children: [
            new TextRun({
              text: `AVVISO A TUTTI I FORNITORI`,
              font: "Calibri",
              color: "FF0000",
              bold: true,
              allCaps: true,
              size: 28 * 2,
              underline: {
                color: "FF0000",
                type: UnderlineType.SINGLE,
              },
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 500 },
          children: [
            new TextRun({
              text: `PRIMA DI ACCEDERE ALLE PARTI COMUNI DEL CONDOMINIO E' NECESSARIO VISUALIZZARE L'INFORMATIVA SULLA SICUREZZA E I DIVIETI`,
              font: "Calibri",
              color: "FF0000",
              bold: true,
              allCaps: true,
              size: 22 * 2,
            }),
          ],
        }),
        new Paragraph(image),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 2500 },
          children: [
            new TextRun({
              text: `VIDEO INFORMATIVO AMMINISTRATORE ${this.company.get("name")}`,
              font: "Times New Roman",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 12 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_2,
          children: [
            new TextRun({
              text: `${this.company.get("legalAddress.route")}, ${this.company.get(
                "legalAddress.streetNumber"
              )}`,
              font: "Times New Roman",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 12 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 2000, after: 200 },
          children: [
            new TextRun({
              text: `SI PREGA DI INQUADRARE IL QR-CODE DIRETTAMENTE CON LA FOTOCAMERA DEL VOSTRO DISPOSITIVO (SE ABILITATO) O ATTRAVERSO UN'APPLICAZIONE DEDICATA.`,
              font: "Calibri",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 14 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_3,
          spacing: { after: 200 },
          children: [
            new TextRun({
              text: `DOPO L'INQUADRATURA IL VIDEO SARA' DISPONIBILE PREVIA BREVE REGISTRAZIONE CON INSERIMENTO DI NOME, COGNOME, NOME AZIENDA.`,
              font: "Calibri",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 14 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_3,
          children: [
            new TextRun({
              text: `BUONA VISIONE`,
              font: "Calibri",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 14 * 2,
            }),
          ],
        }),
      ],
    });

    // Used to export the file into a .docx file
    Packer.toBlob(doc).then((blob) => {
      FileSaver.saveAs(blob, `Cartello-Video-Sicurezza-Fornitori_${this.company.get("name")}.docx`);
    });
  }

  /**
   * Handle Qrcode send by email
   */
  onSafetyVideoSuppliersSendQrcodeByEmail(): void {
    const qrImage = document
      .getElementById("safetyVideoSuppliersQrcodeDataImage")
      .lastElementChild.lastElementChild.getAttribute("src");

    // Open dialog
    const newMessageDialogRef = this.newMessageDialog.open(
      AddCommunicationDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          qrImage,
          phonebook: this.company.get("phonebook"),
        },
        disableClose: true,
      }
    );

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    newMessageDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.toastrService.success(
          this.translateService.instant("communications.add.sendedSuccessfully")
        );
      });
  }

  /**
   * Export safetyVideoSuppliers in a file with given format
   *
   * @since 1.0.0
   */
  onExportSafetyVideoSuppliers(): void {
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      "companies.detail.exportSafetyVideoSuppliersFile"
    )} ${date}`;
    this.subscriptions.push(
      this.videoViewService.getSuppliersVideoExport(this.company.get('_id')).subscribe(
        (res) => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: "application/vnd.ms-excel",
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  
  /**
   * Init safetyVideoResidents data table
   *
   * @since 1.0.0
   */
  initSafetyVideoResidentsDataTable(): void {
    // Load first set of data
    this.loadSafetyVideoResidentsPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.safetyVideoResidentsSort.sortChange.subscribe(
        () => (this.safetyVideoResidentsPaginator.pageIndex = 0)
      )
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.safetyVideoResidentsSort.sortChange, this.safetyVideoResidentsPaginator.page)
        .pipe(tap(() => this.loadSafetyVideoResidentsPage()))
        .subscribe()
    );

    // // Handle server-side filtering through general search
    // this.subscriptions.push(
    //   fromEvent(this.safetyVideoResidentsInput.nativeElement, "keyup")
    //     .pipe(
    //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
    //       distinctUntilChanged(), // Eliminate duplicate values
    //       tap(() => {
    //         this.safetyVideoResidentsPaginator.pageIndex = 0; // Reset page
    //         this.loadSafetyVideoResidentsPage();
    //       })
    //     )
    //     .subscribe()
    // );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    combineLatest([
      this.safetyVideoResidentsFirstNameFilter.valueChanges.pipe(startWith(null as string)),
      this.safetyVideoResidentsLastNameFilter.valueChanges.pipe(startWith(null as string)),
      this.safetyVideoResidentsCompanyNameFilter.valueChanges.pipe(startWith(null as string)),
      this.safetyVideoResidentsCreatedAtFilter.valueChanges.pipe(startWith(null as string)),
    ])
      .pipe(
        debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
        distinctUntilChanged() // Eliminate duplicate values
      )
      .subscribe((res) => {
        // Load page only if at least one value is not null
        if (!res.every((value) => value === null)) {
          this.loadSafetyVideoResidentsPage();
        }
      });
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadSafetyVideoResidentsPage(): void {
    const start = this.safetyVideoResidentsPaginator.pageIndex * this.safetyVideoResidentsPaginator.pageSize;
    const length = this.safetyVideoResidentsPaginator.pageSize;
    const search = {
      firstName: this.safetyVideoResidentsFirstNameFilter.value,
      lastName: this.safetyVideoResidentsLastNameFilter.value,
      companyName: this.safetyVideoResidentsCompanyNameFilter.value,
      createdAt: this.safetyVideoResidentsCreatedAtFilter.value,
    };
    this.safetyVideoResidentsDataSource.loadResidentsVideoItems(
      this.company.get('_id'),
      start,
      length,
      this.safetyVideoResidentsSort.active,
      this.safetyVideoResidentsSort.direction,
      search
    );
  }

  
  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFiltersSafetyVideoResidents(): void {
    // Reset lists service data
    this.listsService.setListSetting("safetyVideoResidents", 0, 10, {});

    // Reset tables' filters
    // this.safetyVideoResidentsInput.nativeElement.value = "";
    this.safetyVideoResidentsFirstNameFilter.setValue(null);
    this.safetyVideoResidentsLastNameFilter.setValue(null);
    this.safetyVideoResidentsCompanyNameFilter.setValue(null);
    this.safetyVideoResidentsCreatedAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.safetyVideoResidentsSort.direction = "asc";
    this.safetyVideoResidentsSort.active = "createdAt";
    this.safetyVideoResidentsSort._stateChanges.next();

    // Reset safetyVideoResidentsPaginator
    this.safetyVideoResidentsPaginator.pageIndex = 0;
    this.safetyVideoResidentsPaginator.pageSize = 10;

    // Reload table's data
    this.loadSafetyVideoResidentsPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilterSafetyVideoResidents(filter: FormControl): void {
    filter.reset();
    this.loadSafetyVideoResidentsPage();
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInputSafetyVideoResidents(): void {
    this.safetyVideoResidentsInput.nativeElement.value = "";
    const event = new KeyboardEvent("keyup");
    this.safetyVideoResidentsInput.nativeElement.dispatchEvent(event);
  }
  
  /**
   * Handle click open QRcode safetyVideoResidents dialogbox
   *
   * @since 1.0.0
   */
  onSafetyVideoResidentsPrintQRcode(): void {
    this.dialog.open(PrintQrcodeDialogComponent, {
      data: {
        qrCode: this.safetyVideoResidentsQrcodeData,
        name: this.companyData.name,
        city: this.companyData.legalAddress.city,
        address: `${this.companyData.legalAddress.route} ${this.companyData.legalAddress.streetNumber}`
      },
      width: "700px",
    });
  }

  /**
   * Handle generate QRcode docx
   *
   * @since 1.0.0
   */
  onSafetyVideoResidentsGenerateQrCodeDoc(): void {
    const qrImage = document
      .getElementById("safetyVideoResidentsQrcodeDataImage")
      .lastElementChild.lastElementChild.getAttribute("src");

    // Create document data
    const doc = new Document();
    const image = Media.addImage(doc, qrImage, 150, 150, {
      floating: {
        horizontalPosition: {
          relative: HorizontalPositionRelativeFrom.PAGE,
          align: HorizontalPositionAlign.CENTER,
        },
        verticalPosition: {
          relative: VerticalPositionRelativeFrom.PARAGRAPH,
          align: VerticalPositionAlign.CENTER,
        },
      },
    });

    doc.addSection({
      properties: {},
      children: [
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 400 },
          children: [
            new TextRun({
              text: `AMMINISTRATORE ${this.company.get("name")}`,
              bold: true,
              allCaps: true,
              color: "auto",
              size: 24 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.TITLE,
          children: [
            new TextRun({
              text: `ATTENZIONE`,
              font: "Calibri",
              color: "FF0000",
              bold: true,
              allCaps: true,
              size: 28 * 2,
              underline: {
                color: "FF0000",
                type: UnderlineType.SINGLE,
              },
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.TITLE,
          spacing: { after: 500 },
          children: [
            new TextRun({
              text: `AVVISO A TUTTI I CONDOMINI`,
              font: "Calibri",
              color: "FF0000",
              bold: true,
              allCaps: true,
              size: 28 * 2,
              underline: {
                color: "FF0000",
                type: UnderlineType.SINGLE,
              },
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 500 },
          children: [
            new TextRun({
              text: `PRIMA DI ACCEDERE ALLE PARTI COMUNI DEL CONDOMINIO E' NECESSARIO VISUALIZZARE L'INFORMATIVA SULLA SICUREZZA E I DIVIETI`,
              font: "Calibri",
              color: "FF0000",
              bold: true,
              allCaps: true,
              size: 22 * 2,
            }),
          ],
        }),
        new Paragraph(image),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 2500 },
          children: [
            new TextRun({
              text: `VIDEO INFORMATIVO AMMINISTRATORE ${this.company.get("name")}`,
              font: "Times New Roman",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 12 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_2,
          children: [
            new TextRun({
              text: `${this.company.get("legalAddress.route")}, ${this.company.get(
                "legalAddress.streetNumber"
              )}`,
              font: "Times New Roman",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 12 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 2000, after: 200 },
          children: [
            new TextRun({
              text: `SI PREGA DI INQUADRARE IL QR-CODE DIRETTAMENTE CON LA FOTOCAMERA DEL VOSTRO DISPOSITIVO (SE ABILITATO) O ATTRAVERSO UN'APPLICAZIONE DEDICATA.`,
              font: "Calibri",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 14 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_3,
          spacing: { after: 200 },
          children: [
            new TextRun({
              text: `DOPO L'INQUADRATURA IL VIDEO SARA' DISPONIBILE PREVIA BREVE REGISTRAZIONE CON INSERIMENTO DI NOME, COGNOME, NOME AZIENDA.`,
              font: "Calibri",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 14 * 2,
            }),
          ],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          heading: HeadingLevel.HEADING_3,
          children: [
            new TextRun({
              text: `BUONA VISIONE`,
              font: "Calibri",
              color: "auto",
              bold: true,
              allCaps: true,
              size: 14 * 2,
            }),
          ],
        }),
      ],
    });

    // Used to export the file into a .docx file
    Packer.toBlob(doc).then((blob) => {
      FileSaver.saveAs(blob, `Cartello-Video-Sicurezza-Fornitori_${this.company.get("name")}.docx`);
    });
  }

  /**
   * Handle Qrcode send by email
   */
  onSafetyVideoResidentsSendQrcodeByEmail(): void {
    const qrImage = document
      .getElementById("safetyVideoResidentsQrcodeDataImage")
      .lastElementChild.lastElementChild.getAttribute("src");

    // Open dialog
    const newMessageDialogRef = this.newMessageDialog.open(
      AddCommunicationDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          qrImage,
          phonebook: this.company.get("phonebook"),
        },
        disableClose: true,
      }
    );

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    newMessageDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.toastrService.success(
          this.translateService.instant("communications.add.sendedSuccessfully")
        );
      });
  }

  /**
   * Export safetyVideoResidents in a file with given format
   *
   * @since 1.0.0
   */
  onExportSafetyVideoResidents(): void {
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      "companies.detail.exportSafetyVideoResidentsFile"
    )} ${date}`;
    this.subscriptions.push(
      this.videoViewService.getResidentsVideoExport(this.company.get('_id')).subscribe(
        (res) => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: "application/vnd.ms-excel",
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  // ### START - Study archive ###

  /**
   * Init genericDocs data table
   *
   * @since 1.0.0
   */
  initGenericDocsDataTable(): void {
    // Load first set of data
    this.genericDocsLoadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.genericDocsSort.sortChange.subscribe(() => this.genericDocsPaginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.genericDocsSort.sortChange, this.genericDocsPaginator.page)
        .pipe(
          tap(() => this.genericDocsLoadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.genericDocsNameFilter.valueChanges.pipe(startWith(null as string)),
        this.genericDocsDescriptionFilter.valueChanges.pipe(startWith(null as string)),
        this.genericDocsCreatedAtFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(res => {
          // Load page only if at least one value is not null
          if (!res.every(value => value === null)) {
            this.genericDocsLoadPage();
          }
        }
        )
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  genericDocsLoadPage(): void {
    const start = this.genericDocsPaginator.pageIndex * this.genericDocsPaginator.pageSize;
    const length = this.genericDocsPaginator.pageSize;
    const search = {
      name: this.genericDocsNameFilter.value,
      description: this.genericDocsDescriptionFilter.value,
      createdAt: this.genericDocsCreatedAtFilter.value,
    };
    this.genericDocsDataSource.loadDocumentsItems(this.company.get('_id'), start, length, this.genericDocsSort.active, this.genericDocsSort.direction, search);
  }

  /**
   * Export generic docs in a file with given format
   *
   * @since 1.0.0
   */
  onGenericDocsExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('companies.detail.exportGenericDocsFile')} ${date}`;
    this.subscriptions.push(
      this.genericDocsService.getExport(this.company.get('_id')).subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onGenericDocsResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('genericDocs', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.genericDocsNameFilter.setValue(null);
    this.genericDocsDescriptionFilter.setValue(null);
    this.genericDocsCreatedAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.genericDocsSort.direction = "desc";
    this.genericDocsSort.active = "createdAt";
    this.genericDocsSort._stateChanges.next();

    // Reset paginator
    this.genericDocsPaginator.pageIndex = 0;
    this.genericDocsPaginator.pageSize = 10;

    // Reload table's data
    this.genericDocsLoadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onGenericDocsResetFilter(filter: FormControl): void {
    filter.reset();
    this.genericDocsLoadPage();
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  genericDocsResetInput(): void {
    // this.genericDocsInput.nativeElement.value = '';
    const event = new KeyboardEvent('keyup');
    // this.genericDocsInput.nativeElement.dispatchEvent(event);
  }

  /**
   * Handler Add new doc
   */
  onGenericDocsAddDoc(): void {
    const dialogRef = this.dialog.open(GenericDocsAddDocDialogComponent, {
      data: {
        companyId: this.company.get("_id")
      },
      width: "700px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            this.genericDocsLoadPage();
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handler Edit doc
   */
  onGenericDocsEdit(data: any): void {
    const dialogRef = this.dialog.open(GenericDocsEditDocDialogComponent, {
      data,
      width: "700px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            this.genericDocsLoadPage();
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handler Delete doc
   */
  onGenericDocsDelete(docId: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('genericDocs.deleteDoc.title'),
        message: this.translateService.instant('genericDocs.deleteDoc.message'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.close'),
      },
      width: '500px',
      disableClose: true
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res2 => {
          if (res2) {
            this.genericDocsService.delete(this.company.get("_id"), docId)
              .subscribe(
                res => {
                  if (res.status) {
                    this.genericDocsLoadPage();
                  }
                },
                err => {
                  this.helperService.handleError(err);
                }
              )
          }
        }
      );
  }

  /**
   * Handler View standard QRcode doc
   */
  onGenericDocsViewQrcode(data: any): void {
    this.dialog.open(GenericDocsQrCodeDialogComponent, {
      data: {
        data,
        print: false,
      },
      width: "450px",
    });
  }

  /**
   * Handler Print standard QRcode doc
   */
  onGenericDocsPrintQrcode(data: any): void {
    this.dialog.open(GenericDocsQrCodeDialogComponent, {
      data: {
        data,
        print: true,
      },
      width: "450px",
    });
  }

  /**
   * Handler download files
   */
  onGenericDocsDownload(data: any): void {
    this.dialog.open(DocumentDownloadDialogComponent, {
      data: {
        documents: data.files,
      },
      width: "600px",
    });
  }

  /**
   * Update docs list to send by email
   *
   * @since 1.0.0
   */
  onGenericDocSelected(doc: any, checked: boolean): void {
    if (doc && checked) {
      this.selectedGenericDocs.push(doc);
    } else if (doc && !checked) {
      const index = this.selectedGenericDocs.indexOf(doc._id);
      this.selectedGenericDocs.splice(index, 1);
    }
  }

  /**
   * Handel send docs by email
   */
  onGenericDocsSendByEmail(): void {
    let body = `<ul>`;
    this.selectedGenericDocs.forEach((doc: any) => {
      if (!doc.files || !doc.files[0]) {
        // This would never happen
        this.toastrService.error("common.errors.unknown");
        return;
      }
      const name = doc.files[0].originalName;
      const path = doc.files[0].path.replace(/src[\\\/]assets/g, 'public');
      let url = "";
      if (path.substring(0, 22) === "https://app.archedi.it") {
        // url = path;
        const docFilename = path.substr(path.lastIndexOf("/"));
        url = `https://api.archedi.it/uploads/genericdocs/companies/${this.company.get("_id")}/${docFilename}`;
      } else {
        url = `${this.configService.apiHost}/${path}`;
      }
      body = body.concat(
        `<li>${name}: <a href="${url}">${url}</a></li><br>`
      );
    });
    body = body.concat(`</ul>`);
    
    // Open dialog
    const newMessageDialogRef = this.newMessageDialog.open(
      AddCommunicationDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          body,
          phonebook: this.company.get("phonebook"),
          sendDocs: true,
        },
        disableClose: true,
      }
    );

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    newMessageDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.toastrService.success(
          this.translateService.instant("communications.add.sendedSuccessfully")
        );
      });
  }
  
  // ### END - Study archive ###



  
  // ### START - Safety Fire ###

  /**
   * Init fireSafetyDocs data table
   *
   * @since 1.0.0
   */
  initFireSafetyDocsDataTable(): void {
    // Load first set of data
    this.fireSafetyDocsLoadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.fireSafetyDocsSort.sortChange.subscribe(() => this.fireSafetyDocsPaginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.fireSafetyDocsSort.sortChange, this.fireSafetyDocsPaginator.page)
        .pipe(
          tap(() => this.fireSafetyDocsLoadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.fireSafetyDocsNameFilter.valueChanges.pipe(startWith(null as string)),
        this.fireSafetyDocsDescriptionFilter.valueChanges.pipe(startWith(null as string)),
        this.fireSafetyDocsCreatedAtFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(res => {
          // Load page only if at least one value is not null
          if (!res.every(value => value === null)) {
            this.fireSafetyDocsLoadPage();
          }
        }
        )
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  fireSafetyDocsLoadPage(): void {
    const start = this.fireSafetyDocsPaginator.pageIndex * this.fireSafetyDocsPaginator.pageSize;
    const length = this.fireSafetyDocsPaginator.pageSize;
    const search = {
      name: this.fireSafetyDocsNameFilter.value,
      description: this.fireSafetyDocsDescriptionFilter.value,
      createdAt: this.fireSafetyDocsCreatedAtFilter.value,
    };
    this.fireSafetyDocsDataSource.loadDocumentsItems(this.company.get('_id'), start, length, this.fireSafetyDocsSort.active, this.fireSafetyDocsSort.direction, search);
  }

  /**
   * Export generic docs in a file with given format
   *
   * @since 1.0.0
   */
  onFireSafetyDocsExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('companies.detail.exportFireSafetyDocsFile')} ${date}`;
    this.subscriptions.push(
      this.fireSafetyDocsService.getExport(this.company.get('_id')).subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onFireSafetyDocsResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('fireSafetyDocs', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.fireSafetyDocsNameFilter.setValue(null);
    this.fireSafetyDocsDescriptionFilter.setValue(null);
    this.fireSafetyDocsCreatedAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.fireSafetyDocsSort.direction = "desc";
    this.fireSafetyDocsSort.active = "createdAt";
    this.fireSafetyDocsSort._stateChanges.next();

    // Reset paginator
    this.fireSafetyDocsPaginator.pageIndex = 0;
    this.fireSafetyDocsPaginator.pageSize = 10;

    // Reload table's data
    this.fireSafetyDocsLoadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onFireSafetyDocsResetFilter(filter: FormControl): void {
    filter.reset();
    this.fireSafetyDocsLoadPage();
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  fireSafetyDocsResetInput(): void {
    // this.fireSafetyDocsInput.nativeElement.value = '';
    const event = new KeyboardEvent('keyup');
    // this.fireSafetyDocsInput.nativeElement.dispatchEvent(event);
  }

  /**
   * Handler Add new doc
   */
  onFireSafetyDocsAddDoc(): void {
    const dialogRef = this.dialog.open(FireSafetyDocsAddDocDialogComponent, {
      data: {
        companyId: this.company.get("_id")
      },
      width: "700px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            this.fireSafetyDocsLoadPage();
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handler Edit doc
   */
  onFireSafetyDocsEdit(data: any): void {
    const dialogRef = this.dialog.open(FireSafetyDocsEditDocDialogComponent, {
      data,
      width: "700px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            this.fireSafetyDocsLoadPage();
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handler Delete doc
   */
  onFireSafetyDocsDelete(docId: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('companies.detail.fireSafetyDocsCard.dialog.deleteDoc.title'),
        message: this.translateService.instant('companies.detail.fireSafetyDocsCard.dialog.deleteDoc.message'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.close'),
      },
      width: '500px',
      disableClose: true
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res2 => {
          if (res2) {
            this.fireSafetyDocsService.delete(this.company.get("_id"), docId)
              .subscribe(
                res => {
                  if (res.status) {
                    this.fireSafetyDocsLoadPage();
                  }
                },
                err => {
                  this.helperService.handleError(err);
                }
              )
          }
        }
      );
  }

  /**
   * Handler View standard QRcode doc
   */
  onFireSafetyDocsViewQrcode(data: any): void {
    this.dialog.open(FireSafetyDocsQrCodeDialogComponent, {
      data: {
        data,
        print: false,
      },
      width: "450px",
    });
  }

  /**
   * Handler Print standard QRcode doc
   */
  onFireSafetyDocsPrintQrcode(data: any): void {
    this.dialog.open(FireSafetyDocsQrCodeDialogComponent, {
      data: {
        data,
        print: true,
      },
      width: "450px",
    });
  }

  /**
   * Handler download files
   */
  onFireSafetyDocsDownload(data: any): void {
    this.dialog.open(DocumentDownloadDialogComponent, {
      data: {
        documents: data.files,
      },
      width: "600px",
    });
  }
  
  /**
   * Update docs list to send by email
   *
   * @since 1.0.0
   */
  onFireSafetyDocSelected(doc: any, checked: boolean): void {
    if (doc && checked) {
      this.selectedFireSafetyDocs.push(doc);
    } else if (doc && !checked) {
      const index = this.selectedFireSafetyDocs.indexOf(doc._id);
      this.selectedFireSafetyDocs.splice(index, 1);
    }
  }

  onFireSafetyDefaultDocSendByEmail(element: any): void {
    const insertedDocs: any[] = [];
    const selectedDocs: any[] = [];
    
    selectedDocs.push(element._id);
    const data = {
      document: element.files,
    } 
    insertedDocs.push({
      docId: element._id,
      title: element.name,
      data,
    });

    const newMessageDialogRef = this.newMessageDialog.open(
      SendEmailDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          phonebook: this.company.get("phonebook")
            ? this.company.get("phonebook")
            : [],
          insertedDocs,
          selectedDocs,
          companyData: this.loggedUserMaster ? this.companyData : "",
          propertyName: "",
          propertyId: "",
          sentFromSupplierPage: false,
          // body: "testo predefinito",
        },
        disableClose: true,
      }
    );

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    newMessageDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.toastrService.success(
          this.translateService.instant("communications.add.sendedSuccessfully")
        );
      });
  }

  /**
   * Handel send docs by email
   */
  onFireSafetyDocsSendByEmail(): void {
    // let body = `<ul>`;
    // this.selectedFireSafetyDocs.forEach((doc: any) => {
    //   if (!doc.files || !doc.files[0]) {
    //     // This would never happen
    //     this.toastrService.error("common.errors.unknown");
    //     return;
    //   }
    //   const name = doc.files[0].originalName;
    //   const path = doc.files[0].path.replace(/src[\\\/]assets/g, 'public');
    //   let url = "";
    //   if (path.substring(0, 22) === "https://app.archedi.it") {
    //     // url = path;
    //     const docFilename = path.substr(path.lastIndexOf("/"));
    //     url = `https://api.archedi.it/uploads/firesafetydocs/companies/${this.company.get("_id")}/${docFilename}`;
    //   } else {
    //     url = `${this.configService.apiHost}/${path}`;
    //   }
    //   body = body.concat(
    //     `<li>${name}: <a href="${url}">${url}</a></li><br>`
    //   );
    // });
    // body = body.concat(`</ul>`);
    
    // Open dialog
    // const newMessageDialogRef = this.newMessageDialog.open(
    //   AddCommunicationDialogComponent,
    //   {
    //     minWidth: "60%",
    //     maxHeight: "98vh",
    //     data: {
    //       body,
    //       phonebook: this.company.get("phonebook"),
    //       sendDocs: true,
    //     },
    //     disableClose: true,
    //   }
    // );
    const insertedDocs: any[] = [];
    const selectedDocs: any[] = [];
    this.selectedFireSafetyDocs.forEach((doc: any) => {
      selectedDocs.push(doc._id);
      const data = {
        document: doc.files,
      } 
      insertedDocs.push({
        docId: doc._id,
        title: doc.name,
        data,
      });
    });

    const newMessageDialogRef = this.newMessageDialog.open(
      SendEmailDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          phonebook: this.company.get("phonebook")
            ? this.company.get("phonebook")
            : [],
          insertedDocs,
          selectedDocs,
          companyData: this.loggedUserMaster ? this.companyData : "",
          propertyName: "",
          propertyId: "",
          sentFromSupplierPage: false,
          // body: "testo predefinito",
        },
        disableClose: true,
      }
    );

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    newMessageDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.toastrService.success(
          this.translateService.instant("communications.add.sendedSuccessfully")
        );
      });
  }

  // ### END - Fire Safety ###

  // ### START - Privacy ###

  /**
   * Init privacyDocs data table
   *
   * @since 1.0.0
   */
  initPrivacyDocsDataTable(): void {
    if (this.hiddenCardPrivacy) {
      // Privacy / Privacy SAG / Nota informativa not active for this company: skip the API call entirely
      return;
    }

    // Load first set of data
    this.privacyDocsLoadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.privacyDocsSort.sortChange.subscribe(() => this.privacyDocsPaginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.privacyDocsSort.sortChange, this.privacyDocsPaginator.page)
        .pipe(
          tap(() => this.privacyDocsLoadPage())
        )
        .subscribe()
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  privacyDocsLoadPage(): void {
    const start = this.privacyDocsPaginator.pageIndex * this.privacyDocsPaginator.pageSize;
    const length = this.privacyDocsPaginator.pageSize;
    // Companies with "privacySag" see the property and supplier privacy docs; the back-end only
    // supports filtering by a single type value, so both are requested together and
    // filtered/paginated client-side (@see PrivacyDocsDataSource). Without "privacySag":
    // "suppliers" shows the supplier doc, the "Nota informativa" shows the condo-owner
    // access key (registered by the back-end on the first generation of the Nota).
    let search: { type: string } | undefined;
    let allowedTypes: string[] | undefined;
    if (this.privacySagActive) {
      allowedTypes = [PROPERTY_PRIVACY_TEMPLATE, SUPPLIER_PRIVACY_TEMPLATE];
    } else {
      const types: string[] = [];
      if (this.informativeNoteActive) {
        types.push(PROPERTY_PRIVACY_TEMPLATE);
      }
      if (this.suppliers) {
        types.push(SUPPLIER_PRIVACY_TEMPLATE);
      }
      if (types.length > 1) {
        allowedTypes = types;
      } else if (types.length === 1) {
        search = { type: types[0] };
      }
    }
    this.privacyDocsDataSource.loadDocumentsItems(this.company.get('_id'), start, length, this.privacyDocsSort.active, this.privacyDocsSort.direction, search, allowedTypes);
  }

  /**
   * Init the "Nota informativa" docs data table (condo-owner privacy notice only)
   *
   * @since 3.0.0
   */
  initInformativeNoteDocsDataTable(): void {
    if (!this.informativeNoteActive || !this.informativeNoteDocsSort || !this.informativeNoteDocsPaginator) {
      // "Nota informativa" not active (or its card is not rendered): skip the API call entirely
      return;
    }

    // Load first set of data
    this.informativeNoteDocsLoadPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.informativeNoteDocsSort.sortChange.subscribe(() => this.informativeNoteDocsPaginator.pageIndex = 0)
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.informativeNoteDocsSort.sortChange, this.informativeNoteDocsPaginator.page)
        .pipe(
          tap(() => this.informativeNoteDocsLoadPage())
        )
        .subscribe()
    );
  }

  /**
   * Load requested "Nota informativa" docs data page
   *
   * @since 3.0.0
   */
  informativeNoteDocsLoadPage(): void {
    const start = this.informativeNoteDocsPaginator.pageIndex * this.informativeNoteDocsPaginator.pageSize;
    const length = this.informativeNoteDocsPaginator.pageSize;
    this.informativeNoteDocsDataSource.loadDocumentsItems(
      this.company.get('_id'),
      start,
      length,
      this.informativeNoteDocsSort.active,
      this.informativeNoteDocsSort.direction,
      { type: PROPERTY_PRIVACY_TEMPLATE }
    );
  }

  /**
   * Check that a privacy doc link is a safe, absolute http(s) URL
   * before using it to navigate or render it inside the UI
   *
   * @since 1.0.0
   */
  isValidPrivacyDocLink(link: string | null): boolean {
    if (!link) {
      return false;
    }
    try {
      const url = new URL(link);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (err) {
      return false;
    }
  }

  /**
   * Build the public, unauthenticated URL for this privacy doc's general
   * access page (@see PrivacyDocsPublicAccessComponent): the visitor lands
   * there, enters a property's tax code, and downloads the document once
   * it's confirmed to belong to this company.
   *
   * @since 1.0.0
   */
  buildPrivacyDocPublicLink(docType: string): string | null {
    console.log("docType", docType)
    const companyId = this.company.get('_id');
    const segmentByType: { [key: string]: string } = {
      "informativa_privacy_immobile": 'condo-owner',
      "informativa_privacy_fornitore": 'supplier',
    };
    const segment = segmentByType[docType];
    if (!companyId || !segment) {
      return null;
    }
    return `${window.location.origin}/privacy-docs/${companyId}/${segment}`;
  }

  /**
   * Handle "Accedi" action: open the privacy doc's public access page in a new tab
   *
   * @since 1.0.0
   */
  onPrivacyDocsAccess(element: any): void {
    const link = this.buildPrivacyDocPublicLink(element && element.type);
    if (!link || !this.isValidPrivacyDocLink(link)) {
      this.toastrService.error(this.translateService.instant('companies.detail.privacyDocsCard.invalidLink'));
      return;
    }
    window.open(link, '_blank', 'noopener,noreferrer');
  }

  /**
   * Handle "Visualizza QR-code" action: open a dialog showing the QR-code
   * that encodes the privacy doc's public access page link
   *
   * @since 1.0.0
   */
  onPrivacyDocsViewQrcode(element: any): void {
    const link = this.buildPrivacyDocPublicLink(element && element.type);
    if (!link || !this.isValidPrivacyDocLink(link)) {
      this.toastrService.error(this.translateService.instant('companies.detail.privacyDocsCard.invalidLink'));
      return;
    }
    this.dialog.open(PrivacyDocsQrCodeDialogComponent, {
      minWidth: '40%',
      data: {
        data: { ...element, link },
      },
    });
  }

  /**
   * Handle "Visualizza link" action: open a dialog showing the privacy doc's
   * public access page link
   *
   * @since 1.0.0
   */
  onPrivacyDocsViewLink(element: any): void {
    const link = this.buildPrivacyDocPublicLink(element && element.type);
    if (!link || !this.isValidPrivacyDocLink(link)) {
      this.toastrService.error(this.translateService.instant('companies.detail.privacyDocsCard.invalidLink'));
      return;
    }
    this.dialog.open(PrivacyDocsLinkDialogComponent, {
      minWidth: '40%',
      data: {
        data: { ...element, link },
      },
    });
  }

  /**
   * Handle "Genera documenti" action: let the user pick which properties
   * of this company to generate the privacy documents for, then trigger
   * the generation on the back-end and download the resulting file
   *
   * @since 1.0.0
   */
  onPrivacyDocsGenerate(): void {
    if (!this.privacySagActive && !this.suppliers) {
      return;
    }

    if (!this.availableProperties || !this.availableProperties.length) {
      this.toastrService.error(
        this.translateService.instant('companies.detail.privacyDocsCard.generatePropertiesDialog.noProperties')
      );
      return;
    }

    const dialogRef = this.dialog.open(GeneratePrivacyDocsDialogComponent, {
      data: {
        modalTitle: this.translateService.instant('companies.detail.privacyDocsCard.generatePropertiesDialog.title'),
        properties: this.availableProperties,
      },
      width: '800px',
      disableClose: true
    });

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(take(1))
      .subscribe((res2: { propertyIds: string[] }) => {
        if (!res2 || !res2.propertyIds || !res2.propertyIds.length) {
          return;
        }
        // Companies with only "privacy" + "suppliers" (not "privacySag") can only generate the supplier privacy doc
        const templateType = this.privacySagActive ? undefined : SUPPLIER_PRIVACY_TEMPLATE;
        this.privacyDocsService.generateDocuments(this.company.get('_id'), res2.propertyIds, templateType)
          .subscribe(
            (fileBlob: Blob) => {
              const extension = PRIVACY_DOC_MIME_EXTENSIONS[fileBlob.type] || DEFAULT_PRIVACY_DOC_EXTENSION;
              this.toastrService.success(
                this.translateService.instant('companies.detail.privacyDocsCard.generatePropertiesDialog.success')
              );
              if (extension === 'zip') {
                return;
              }
              const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
              const fileName = `${this.translateService.instant(
                'companies.detail.privacyDocsCard.generatePropertiesDialog.exportFileName'
              )}-${date}.${extension}`;
              FileSaver.saveAs(fileBlob, fileName);
            },
            err => {
              this.helperService.handleError(err);
            }
          );
      });
  }

  /**
   * Reload the tables listing the condo-owner privacy access key: the back-end
   * registers it on the first "Nota informativa" generation
   *
   * @since 3.0.0
   */
  refreshPrivacyAccessKeyTables(): void {
    if (!this.hiddenCardPrivacy && this.privacyDocsPaginator && this.privacyDocsSort) {
      this.privacyDocsLoadPage();
    }
    if (this.informativeNoteActive && this.informativeNoteDocsPaginator && this.informativeNoteDocsSort) {
      this.informativeNoteDocsLoadPage();
    }
  }

  /**
   * Handle "Genera Nota informativa" action: download the generated PDF
   *
   * @since 3.0.0
   */
  onInformativeNoteDownload(): void {
    if (!this.informativeNoteActive || this.informativeNoteDocIsLoading) {
      return;
    }
    this.informativeNoteDocIsLoading = true;
    this.subscriptions.push(
      this.informativeNoteService.generateDocumentFile(this.company.get('_id'))
        .pipe(finalize(() => this.informativeNoteDocIsLoading = false))
        .subscribe(
          res => {
            FileSaver.saveAs(res.blob, res.fileName);
            this.refreshPrivacyAccessKeyTables();
          },
          err => this.helperService.handleError(err)
        )
    );
  }

  /**
   * Handle "Invia Nota informativa" action: generate the document, upload it and
   * open the email dialog with the document already attached
   *
   * @since 3.0.0
   */
  onInformativeNoteSendByEmail(): void {
    if (!this.informativeNoteActive || this.informativeNoteDocIsLoading) {
      return;
    }
    this.informativeNoteDocIsLoading = true;
    this.subscriptions.push(
      this.informativeNoteService.generateDocumentFile(this.company.get('_id'))
        .pipe(
          tap(() => this.refreshPrivacyAccessKeyTables()),
          concatMap(res => this.informativeNoteService.uploadForEmail(res.blob, res.fileName)),
          finalize(() => this.informativeNoteDocIsLoading = false)
        )
        .subscribe(
          (files: any[]) => {
            const dialogRef = this.newMessageDialog.open(SendEmailDialogComponent, {
              minWidth: "60%",
              maxHeight: "98vh",
              data: {
                phonebook: this.company.get("phonebook") ? this.company.get("phonebook") : [],
                insertedDocs: [],
                selectedDocs: [],
                attachments: files,
                hideInsertedDocs: true,
                companyData: this.loggedUserMaster ? this.companyData : "",
                propertyName: "",
                propertyId: "",
                sentFromSupplierPage: false,
              },
              disableClose: true,
            });
            dialogRef.beforeClosed()
              .pipe(take(1))
              .subscribe(res => {
                if (res) {
                  this.toastrService.success(this.translateService.instant("communications.add.sendedSuccessfully"));
                }
              });
          },
          err => this.helperService.handleError(err)
        )
    );
  }

  /**
   * Handle "Invia PEC" action: when plain Privacy is active (not Privacy SAG),
   * first check the percentage of suppliers that already have their privacy
   * doc generated and warn the user if it's below 80%; in any case, ask for
   * confirmation before triggering the mass sending of the privacy documents
   * access PEC email to every supplier of this company
   *
   * @since 1.0.0
   */
  onPrivacyDocsSendPec(): void {
    if (this.privacySagActive) {
      this.openPrivacyDocsSendPecConfirmDialog();
      return;
    }

    this.privacyDocsSendPecDisabled = true;
    this.subscriptions.push(
      this.privacyDocsService.getPercentage(this.company.get('_id'), SUPPLIER_PRIVACY_TEMPLATE)
        .pipe(finalize(() => this.privacyDocsSendPecDisabled = false))
        .subscribe(
          res => {
            const percentage = res && res.data ? res.data.percentage : null;
            this.openPrivacyDocsSendPecConfirmDialog(percentage);
          },
          err => {
            this.helperService.handleError(err);
          }
        )
    );
  }

  /**
   * Open the "Invia PEC" confirmation dialog and, once confirmed, trigger
   * the mass sending of the privacy documents access PEC email to every
   * supplier of this company
   *
   * @since 1.0.0
   *
   * @param [percentage] Percentage of suppliers with their privacy doc
   *                      already generated; when below 80 a stronger
   *                      warning message is shown to the user
   */
  private openPrivacyDocsSendPecConfirmDialog(percentage: number | null = null): void {
    const lowPercentage = typeof percentage === 'number' && percentage < 80;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.confirm.title'),
        message: lowPercentage
          ? this.translateService.instant('companies.detail.privacyDocsCard.sendPecLowPercentageConfirm', { percentage })
          : this.translateService.instant('companies.detail.privacyDocsCard.sendPecConfirm'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.undo'),
      },
      width: '500px',
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(take(1))
      .subscribe(res => {
        if (!res) {
          return;
        }

        this.privacyDocsSendPecDisabled = true;
        this.subscriptions.push(
          this.suppliersService.sendPrivacyEmailAll(this.company.get('_id'))
            .pipe(finalize(() => this.privacyDocsSendPecDisabled = false))
            .subscribe(
              res2 => {
                this.toastrService.success(res2.message);
              },
              err => {
                this.helperService.handleError(err);
              }
            )
        );
      });
  }

  // ### END - Privacy ###

  // ### START - Privacy Studio Docs ###

  /**
   * Handle "Scarica template" action: open a dialog listing the
   * company-level ("studio") privacy document templates available for
   * download, pre-filled with this company's own registry data
   * (Privacy SAG subscription required, gated in the template via
   * privacySagActive)
   *
   * @since 1.0.0
   */
  onOpenStudioPrivacyTemplatesDialog(): void {
    if (!this.privacySagActive) {
      return;
    }

    this.dialog.open(StudioPrivacyTemplatesDialogComponent, {
      data: {
        companyId: this.company.get("_id"),
      },
      width: "650px",
      disableClose: true,
    });
  }

  /**
   * Init privacyStudioDocs data table
   *
   * @since 1.0.0
   */
  initPrivacyStudioDocsDataTable(): void {
    if (this.hiddenCardPrivacy) {
      // Privacy / Privacy SAG not active for this company: skip the API call entirely
      return;
    }

    // Load first set of data
    this.privacyStudioDocsLoadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.privacyStudioDocsSort.sortChange.subscribe(() => this.privacyStudioDocsPaginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.privacyStudioDocsSort.sortChange, this.privacyStudioDocsPaginator.page)
        .pipe(
          tap(() => this.privacyStudioDocsLoadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.privacyStudioDocsTypeFilter.valueChanges.pipe(startWith(null as string)),
        this.privacyStudioDocsDescriptionFilter.valueChanges.pipe(startWith(null as string)),
        this.privacyStudioDocsCreatedAtFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(res => {
          // Load page only if at least one value is not null
          if (!res.every(value => value === null)) {
            this.privacyStudioDocsLoadPage();
          }
        }
        )
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  privacyStudioDocsLoadPage(): void {
    const start = this.privacyStudioDocsPaginator.pageIndex * this.privacyStudioDocsPaginator.pageSize;
    const length = this.privacyStudioDocsPaginator.pageSize;
    const search = {
      type: this.privacyStudioDocsTypeFilter.value,
      description: this.privacyStudioDocsDescriptionFilter.value,
      createdAt: this.privacyStudioDocsCreatedAtFilter.value,
    };
    this.privacyStudioDocsDataSource.loadDocumentsItems(this.company.get('_id'), start, length, this.privacyStudioDocsSort.active, this.privacyStudioDocsSort.direction, search);
  }

  /**
   * Export privacy studio docs in a file with given format
   *
   * @since 1.0.0
   */
  onPrivacyStudioDocsExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('companies.detail.exportPrivacyStudioDocsFile')} ${date}`;
    this.subscriptions.push(
      this.privacyStudioDocsService.getExport(this.company.get('_id')).subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onPrivacyStudioDocsResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('privacyStudioDocs', 0, 10, {});

    // Reset tables' filters
    this.privacyStudioDocsTypeFilter.setValue(null);
    this.privacyStudioDocsDescriptionFilter.setValue(null);
    this.privacyStudioDocsCreatedAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.privacyStudioDocsSort.direction = "desc";
    this.privacyStudioDocsSort.active = "createdAt";
    this.privacyStudioDocsSort._stateChanges.next();

    // Reset paginator
    this.privacyStudioDocsPaginator.pageIndex = 0;
    this.privacyStudioDocsPaginator.pageSize = 10;

    // Reload table's data
    this.privacyStudioDocsLoadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onPrivacyStudioDocsResetFilter(filter: FormControl): void {
    filter.reset();
    this.privacyStudioDocsLoadPage();
  }

  /**
   * Handler Add new doc
   */
  onPrivacyStudioDocsAddDoc(): void {
    const dialogRef = this.dialog.open(PrivacyStudioDocsAddDocDialogComponent, {
      data: {
        companyId: this.company.get("_id")
      },
      width: "700px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            this.privacyStudioDocsLoadPage();
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handler Edit doc
   */
  onPrivacyStudioDocsEdit(data: any): void {
    const dialogRef = this.dialog.open(PrivacyStudioDocsEditDocDialogComponent, {
      data,
      width: "700px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            this.privacyStudioDocsLoadPage();
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handler Delete doc
   */
  onPrivacyStudioDocsDelete(docId: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('companies.detail.privacyDocsCard.studioSection.dialog.deleteDoc.title'),
        message: this.translateService.instant('companies.detail.privacyDocsCard.studioSection.dialog.deleteDoc.message'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.close'),
      },
      width: '500px',
      disableClose: true
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res2 => {
          if (res2) {
            this.privacyStudioDocsService.delete(this.company.get("_id"), docId)
              .subscribe(
                res => {
                  if (res.status) {
                    this.privacyStudioDocsLoadPage();
                  }
                },
                err => {
                  this.helperService.handleError(err);
                }
              )
          }
        }
      );
  }

  /**
   * Handler View QRcode doc
   */
  onPrivacyStudioDocsViewQrcode(data: any): void {
    this.dialog.open(PrivacyStudioDocsQrCodeDialogComponent, {
      data: {
        data,
        print: false,
      },
      width: "450px",
    });
  }

  /**
   * Handler download files
   */
  onPrivacyStudioDocsDownload(data: any): void {
    this.dialog.open(DocumentDownloadDialogComponent, {
      data: {
        documents: data.files,
      },
      width: "600px",
    });
  }

  /**
   * Update docs list to send by email
   *
   * @since 1.0.0
   */
  onPrivacyStudioDocSelected(doc: any, checked: boolean): void {
    if (doc && checked) {
      this.selectedPrivacyStudioDocs.push(doc);
    } else if (doc && !checked) {
      const index = this.selectedPrivacyStudioDocs.indexOf(doc._id);
      this.selectedPrivacyStudioDocs.splice(index, 1);
    }
  }

  /**
   * Handle send selected docs by email
   */
  onPrivacyStudioDocsSendByEmail(): void {
    if (!this.selectedPrivacyStudioDocs.length) {
      this.toastrService.error(this.translateService.instant('common.errors.chooseElementFirst'));
      return;
    }

    const insertedDocs: any[] = [];
    const selectedDocs: any[] = [];
    this.selectedPrivacyStudioDocs.forEach((doc: any) => {
      selectedDocs.push(doc._id);
      const data = {
        document: doc.files,
      };
      insertedDocs.push({
        docId: doc._id,
        title: this.translateService.instant(`companies.fields.privacyStudioDocs.types.${doc.type}`),
        data,
      });
    });

    const newMessageDialogRef = this.newMessageDialog.open(
      SendEmailDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          phonebook: this.company.get("phonebook")
            ? this.company.get("phonebook")
            : [],
          insertedDocs,
          selectedDocs,
          companyData: this.loggedUserMaster ? this.companyData : "",
          propertyName: "",
          propertyId: "",
          sentFromSupplierPage: false,
        },
        disableClose: true,
      }
    );

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    newMessageDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.toastrService.success(
          this.translateService.instant("communications.add.sendedSuccessfully")
        );
      });
  }

  // ### END - Privacy Studio Docs ###


  /**
   * open pop up Video
   *
   * @since 1.0.0
   */
  onVideoPopUp(videoType: string): void {
    let title = '';
    let videoName = '';
    switch(videoType) {
      case 'SafetyVideoSuppliers': 
        title = this.translateService.instant("companies.detail.safetyVideoSuppliersCard.title");
        videoName = 'Archedi-SD3';
      break;
      case 'SafetyVideoResidents': 
        title = this.translateService.instant("companies.detail.safetyVideoResidentsCard.title");
        videoName = 'Archedi-Resident';
      break;
    }
    const width = "800px";
    this.dialog.open(VideoDialogComponent, {
      data: { 
        title,
        videoName
      },
      width,
      maxWidth: width,
    });
  }

  /**
   * Reload historical notes
   */
  OnPlatformNoteDeactivate(event: boolean): void {
    if (event) {
      this.historicalNotes.loadPage();
    }
  }

  /**
   * Open modal of property notes
   * 
   * @since 1.0.0
   */
  onOpenNotes(propertyId: string): void {
    this.dialog.open(ViewPlatformNotesDialogComponent, {
      data: {
        propertyId
      },
      width: '90%',
    });
  }

  /**
   * Open modal of company balances notes
   * 
   * @since 1.0.0
   */
  onViewBalancesNotes(): void {
    this.dialog.open(ViewPlatformNotesDialogComponent, {
      data: {
        companyIdBalances: this.companyId
      },
      width: '90%',
    });
  }

  /**
   * Handle list of properties subscription to renew
   *
   * @since 1.0.0
   */
  onSubsSelected(element: any, checked: boolean): void {
    if (element && checked) {
      this.selectedSuppliers.push({
        id: element._id,
        name: element.companyName,
        email: element.pec,
        value: true,
      });
    } else if (element && !checked) {
      const index = this.selectedSuppliers.findIndex(
        (el: any) => el.id.toString() === element._id.toString()
      );
      if (index > -1) {
        this.selectedSuppliers.splice(index, 1);
      }
    }
  }
        
  /**
   * Handle invite to suppliers
   */
  onInviteSuppliers(): void {
    if (!this.selectedSuppliers.length) {
      this.toastrService.error(this.translateService.instant('common.errors.chooseElementFirst'));
      return;
    }

    const sendData = {
      suppliers: this.selectedSuppliers,
      companyId: this.companyId,
      companyName: this.companyData.name,
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(
          "companies.detail.inviteSuppliersAdmin.title"
        ),
        message: this.translateService.instant(
          "companies.detail.inviteSuppliersAdmin.description"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.subscriptions.push(
          this.suppliersService.inviteSuppliers(sendData)
          .subscribe(
            res => {
              if (res.status) {
                this.selectedSuppliers = [];
                this.toastrService.success(res.message);
                this.loadSuppliersPage();
              } else {
                this.helperService.handleError(res.message);
              }
            },
            err => {
              this.helperService.handleError(err);
            }
          )
        );
      });
  }

  
  /**
   * Enable/Disable send supplier email
   *
   * @since 1.0.0
   */
  onToggleSendEmail(action: string): void {
    let data = {
      disableSendEmailNfcAccess: false
    };
    let message = this.translateService.instant(`companies.detail.enableSendEmail`);
    if (action === 'disable') {
      data = {
        disableSendEmailNfcAccess: true
      };
      message = this.translateService.instant(`companies.detail.disableSendEmail`);
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.confirm.title'),
        message,
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.undo'),
      },
      width: '500px',
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res => {
          if (!res) {
            return;
          }
          this.detailCardIsLoading = true;
          this.subscriptions.push(
            this.companiesService.updateFields(this.company.getId(), data)
              .pipe(
                finalize(() => this.detailCardIsLoading = false)
              )
              .subscribe(
                res2 => {
                  this.toastrService.success(res2.message);
                  this.disableSendEmail = res2.data.disableSendEmailNfcAccess;
                  return;
                },
                err => {
                  // Choose one of the following error handling
                  // method. The first one shows a message right
                  // under the form fields (if the form is properly
                  // setted), the second one shows toastr
                  // notifications for each error
                  // this.helperService.handleFormError(this.passwordForm, err);
                  this.helperService.handleError(err);
                }
              )
          );
        }
      );
  }

  /**
   * Toggle visibility of the masterCode (condominium code) column
   * for master/collaborator users, persisting the choice on the company
   *
   * @since 3.0.0
   */
  onToggleShowMasterCode(): void {
    const data = { showMasterCode: !this.showMasterCode };
    const message = this.translateService.instant(
      this.showMasterCode ? 'companies.detail.hideMasterCode' : 'companies.detail.showMasterCode'
    );
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.confirm.title'),
        message,
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.undo'),
      },
      width: '500px',
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res => {
          if (!res) {
            return;
          }
          this.detailCardIsLoading = true;
          this.subscriptions.push(
            this.companiesService.updateFields(this.company.getId(), data)
              .pipe(
                finalize(() => this.detailCardIsLoading = false)
              )
              .subscribe(
                res2 => {
                  this.toastrService.success(res2.message);
                  this.showMasterCode = res2.data.showMasterCode;
                  const masterCodeIndex = this.subPropertiesDisplayedColumns.findIndex(
                    (el: string) => el === 'masterCode'
                  );
                  if (this.showMasterCode && masterCodeIndex === -1) {
                    const nameIndex = this.subPropertiesDisplayedColumns.findIndex(
                      (el: string) => el === 'name'
                    );
                    this.subPropertiesDisplayedColumns.splice(nameIndex + 1, 0, 'masterCode');
                  } else if (!this.showMasterCode && masterCodeIndex > -1) {
                    this.subPropertiesDisplayedColumns.splice(masterCodeIndex, 1);
                  }
                },
                err => {
                  this.helperService.handleError(err);
                }
              )
          );
        }
      );
  }

  /**
   * Show the company's internal ID in an info dialog (Admin/Operatore only,
   * gated in the template via loggedUserAdminOrOperator)
   *
   * @since 3.0.0
   */
  onShowCompanyId(): void {
    this.dialog.open(InfoDialogComponent, {
      data: {
        title: this.translateService.instant('companies.detail.companyIdDialogTitle'),
        message: this.company.getId(),
      },
      width: '400px',
      maxWidth: '400px',
    });
  }

  /**
   * Init invitation data table
   *
   * @since 1.0.0
   */
  initInvitationDataTable(): void {
    // Reset pagination after sorting
    this.subscriptions.push(this.invitationSort.sortChange.subscribe(() => this.invitationPaginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.invitationSort.sortChange, this.invitationPaginator.page)
        .pipe(
          tap(() => this.loadInvitationPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.invitationSupplierFilter.valueChanges.pipe(startWith(null as string)),
        this.invitationEmailFilter.valueChanges.pipe(startWith(null as string)),
        this.invitationPecFilter.valueChanges.pipe(startWith(null as string)),
        this.invitationVatNumberFilter.valueChanges.pipe(startWith(null as string)),
        this.invitationRegisterFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(
          res => {
            // Load page only if at least one value is not null
            if (!res.every((value) => value === null)) {
              this.loadInvitationPage();
            }
          }
        )
    );

    // Load first set of data
    this.loadInvitationPage();
  }
  
  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadInvitationPage(): void {
    const start = this.invitationPaginator.pageIndex * this.invitationPaginator.pageSize;
    const length = this.invitationPaginator.pageSize;
    
    const search = {
      general: '', // this.input.nativeElement.value,
      companyName: this.invitationSupplierFilter.value,
      pec: this.invitationPecFilter.value,
      email: this.invitationEmailFilter.value,
      vatNumber: this.invitationVatNumberFilter.value,
      register: this.invitationRegisterFilter.value,
    };

    const master = true;

    this.invitationDataSource.loadCompanyItems(this.companyId, start, length, this.invitationSort.active, this.invitationSort.direction, search);
  }
  
  /**
   * Export suppliers in a file with given format
   *
   * @since 1.0.0
   */
  onExportInvitation(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('suppliersInvitation.exportFileName')} ${date}`;
    this.subscriptions.push(
      this.suppliersService.getCompanyInvitationExport(this.companyId).subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFiltersInvitaion(): void {
    // Reset lists service data
    this.listsService.setListSetting('', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.invitationSupplierFilter.setValue(null);
    this.invitationEmailFilter.setValue(null);
    this.invitationPecFilter.setValue(null);
    this.invitationVatNumberFilter.setValue(null);
    this.invitationRegisterFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.invitationSort.direction = 'desc';
    this.invitationSort.active = 'companyName';
    this.invitationSort._stateChanges.next();

    // Reset paginator
    this.invitationPaginator.pageIndex = 0;
    this.invitationPaginator.pageSize = 10;

    // Reload table's data
    this.loadInvitationPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetInvitaionFilter(filter: FormControl): void {
    filter.reset();
    this.loadInvitationPage();
  }
   
  /**
   * open pop up info of Registration
   *
   * @since 1.0.0
   */
  onInfoRegistrationPopUp(): void {
    const width = "400px";
    const title = this.translateService.instant('suppliers.fields.registration');
    const message = this.translateService.instant('suppliers.fields.registrationMsg');
    if (title.trim() !== '') {
      this.dialog.open(InfoDialogComponent, {
        data: { 
          title,
          message,
        },
        width,
        maxWidth: width,
      });
    }
  }
     
  /**
   * open pop up info of Durc
   *
   * @since 1.0.0
   */
  onInfoDurcPopUp(): void {
    const width = "400px";
    const title = this.translateService.instant('suppliers.fields.expirationDurc');
    const message = this.translateService.instant('suppliers.fields.expirationDurcMsg');
    if (title.trim() !== '') {
      this.dialog.open(InfoDialogComponent, {
        data: { 
          title,
          message,
        },
        width,
        maxWidth: width,
      });
    }
  }
   
  /**
   * open pop up info of Registration
   *
   * @since 1.0.0
   */
  onInfoCreditLicencePopUp(): void {
    const width = "400px";
    const title = this.translateService.instant('suppliers.fields.iCreditLicence');
    const message = this.translateService.instant('suppliers.fields.iCreditLicenceMsg');
    if (title.trim() !== '') {
      this.dialog.open(InfoDialogComponent, {
        data: { 
          title,
          message,
        },
        width,
        maxWidth: width,
      });
    }
  }
   
  /**
   * open pop up info of Status
   *
   * @since 1.0.0
   */
  onInfoStatusPopUp(): void {
    const width = "400px";
    const title = this.translateService.instant('suppliers.fields.iStatus');
    const message = this.translateService.instant('suppliers.fields.iStatusMsg');
    if (title.trim() !== '') {
      this.dialog.open(InfoDialogComponent, {
        data: { 
          title,
          message,
        },
        width,
        maxWidth: width,
      });
    }
  }

  
  /**
   * Techincal file export in EXCEL format
   *
   * @since 1.0.0
   */
  onExportTechnicalFile(): void {
    // this.exportTechnicalFileIsLoading = true;
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      `companies.list.technicalFile`
    )}  ${this.companyData.name} ${date}`;
    this.subscriptions.push(
      this.companiesService.getTechnicalFile(this.companyId).subscribe(
        (res) => {
            this.toastrService.success(res.message);
        },
        (err) => {
          this.toastrService.error(err.message);
        }
      )
      // this.companiesService.getTechnicalFile(companyId).subscribe(
      //   (res) => {
      //     const fileBlob = res;
      //     const blob = new Blob([fileBlob], {
      //       type: "application/vnd.ms-excel",
      //     });
      //     FileSaver.saveAs(blob, `${fileName}.xlsx`);
      //     this.exportTechnicalFileIsLoading = false;
      //   },
      //   (err) => {
      //     this.helperService.handleError(err);
      //     this.exportTechnicalFileIsLoading = false;
      //   }
      // )
    );
  }
}
